import type * as Types from '../../graphql/generated-base';

import type { DocumentNode } from 'graphql';
import * as Apollo from '@apollo/client';
import * as ApolloReactHooks from '@graphql/react-hooks';
export type TravelOfferFragment = {
  __typename: 'TravelOffer';
  offerToken: string;
  checkIn: string;
  checkOut: string;
  offerGroupId?: string | null;
  providerName?: string | null;
  availability?: number | null;
  components: Array<{
    __typename: 'TravelOfferAccommodationUnitsComponent';
    distributionType?: Types.TravelOfferDistributionType | null;
    componentType: string;
    refundability: Types.TravelOfferRefundability;
    policies: Array<{ __typename?: 'TravelOfferPolicyField'; title: string; description: string }>;
    cancellationPolicies?: Array<{
      __typename?: 'TravelOfferCancellationPolicyRule';
      end?: string | null;
      start: string;
      estimatedCancellationAmount?: {
        __typename?: 'TravelOfferEstimatedCancellationAmount';
        valueType: Types.TravelOfferCancellationPolicyRuleValueType;
        amount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
      } | null;
      estimatedFeeAmount?: {
        __typename?: 'TravelMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      } | null;
    }> | null;
    boardBasis?: { __typename?: 'AccommodationCatalogFacilityOrAmenity'; label: string } | null;
    occupancies: Array<{ __typename?: 'TravelOfferAccommodationUnitsComponentOccupancy'; adults: number; children: number; childrenAges?: Array<number> | null }>;
    unit: {
      __typename: 'AccommodationCatalogPropertyUnit';
      id: string;
      name: string;
      plainTextLongDescription: string;
      facilities: Array<{ __typename: 'AccommodationCatalogFacilityOrAmenity'; label: string }>;
      photos: Array<{ __typename?: 'AccommodationCatalogPhoto'; url: string }>;
      property: {
        __typename?: 'AccommodationCatalogProperty';
        name: string;
        joyPlaceId: string;
        starRating?: number | null;
        address?: {
          __typename?: 'AccommodationCatalogPropertyAddress';
          city?: string | null;
          state?: string | null;
          street?: string | null;
          country?: string | null;
          postalCode?: string | null;
        } | null;
        photos: Array<{
          __typename?: 'AccommodationCatalogPhoto';
          url: string;
          subject: { __typename?: 'AccommodationCatalogPhotoSubject'; label: string; type: Types.AccommodationCatalogPhotoSubjectType };
        }>;
        userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
      };
    };
  }>;
  onsiteCharges: Array<{
    __typename?: 'TravelOfferOnsiteCharge';
    title: string;
    units?: Array<string> | null;
    unitPrice: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
  }>;
  fees: Array<{
    __typename?: 'TravelOfferFee';
    isIncludedInTotal: boolean;
    rate: number;
    type: Types.FeeType;
    visibility: string;
    amount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnitsBigInt: any };
  }>;
  baseAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
  totalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
  allInTotalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
  onsiteChargesTotalAmount?: {
    __typename?: 'TravelMonetaryValue';
    currency: Types.TravelCurrencyCode;
    digits: number;
    floatingPointDecimalString: string;
    valueInMinorUnits: number;
  } | null;
  originalBaseAmount?: {
    __typename?: 'TravelMonetaryValue';
    currency: Types.TravelCurrencyCode;
    digits: number;
    floatingPointDecimalString: string;
    valueInMinorUnits: number;
  } | null;
  originalTotalAmount?: {
    __typename?: 'TravelMonetaryValue';
    currency: Types.TravelCurrencyCode;
    digits: number;
    floatingPointDecimalString: string;
    valueInMinorUnits: number;
  } | null;
  prebookChange?: {
    __typename?: 'TravelOfferPrebookChange';
    priceDifferencePercent: number;
    cancellationChanged: boolean;
    boardChanged: boolean;
    oldAmount?: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number } | null;
    newAmount?: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number } | null;
  } | null;
};

export type TravelOfferReservationForCheckoutFragment = {
  __typename?: 'TravelOfferReservation';
  id: string;
  offerToken: string;
  status: Types.TravelOfferReservationStatus;
  joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
  joyDiscountCoupon?: {
    __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
    couponCode: string;
    discountAmount: number;
    discountCurrency: string;
    discountedBaseCostMinorUnits?: number | null;
    discountedTaxesAndFeesMinorUnits?: number | null;
    isEmailRestricted: boolean;
    scopeType: Types.ScopeTypeDtoEnum;
    bookerType: Types.BookerTypeDtoEnum;
    allowedPaidWith?: Types.CouponPaidWithDtoEnum | null;
    discountBaseMinorUnits?: number | null;
  } | null;
  confirmation?: {
    __typename?: 'TravelOfferReservationConfirmation';
    billingContact: {
      __typename?: 'TravelOfferReservationContactInfo';
      email: string;
      firstName: string;
      lastName: string;
      middleName?: string | null;
      phone: string;
      title: string;
      address: { __typename?: 'TravelReservationAddress'; city: string; country: string; line1: string; line2?: string | null; postalCode?: string | null; state?: string | null };
    };
  } | null;
  roomGuests: Array<{
    __typename?: 'TravelOfferReservationRoomGuests';
    unitId: string;
    guests: Array<{
      __typename?: 'TravelOfferReservationGuestInformation';
      ageClassification: Types.TravelReservationGuestAgeClassification;
      firstName: string;
      lastName: string;
      title?: Types.TravelReservationGuestTitle | null;
    }>;
  }>;
};

export type EventInfoForAccommodationPropertyQueryVariables = Types.Exact<{
  name: Types.Scalars['String'];
}>;

export type EventInfoForAccommodationPropertyQuery = {
  __typename?: 'Query';
  eventByName?: { __typename?: 'Event'; id: string; photo?: { __typename?: 'Photo'; id: string; url: string } | null } | null;
};

export type AccommodationCatalogPropertyDetailsFragment = {
  __typename: 'AccommodationCatalogProperty';
  joyPlaceId: string;
  name: string;
  plainTextLongDescription: string;
  starRating?: number | null;
  userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
  facilities: Array<{ __typename?: 'AccommodationCatalogFacilityOrAmenity'; label: string }>;
  photos: Array<{ __typename?: 'AccommodationCatalogPhoto'; url: string }>;
  location: { __typename?: 'AccommodationCatalogLocation'; latitude?: number | null; longitude?: number | null };
  address?: {
    __typename?: 'AccommodationCatalogPropertyAddress';
    city?: string | null;
    country?: string | null;
    postalCode?: string | null;
    state?: string | null;
    street?: string | null;
  } | null;
};

export type AccommodationCatalogPropertyByIdQueryVariables = Types.Exact<{
  idObject: Types.AccommodationCatalogPropertyIdentifierInput;
}>;

export type AccommodationCatalogPropertyByIdQuery = {
  __typename?: 'Query';
  accommodationCatalogPropertyById: {
    __typename: 'AccommodationCatalogProperty';
    joyPlaceId: string;
    name: string;
    plainTextLongDescription: string;
    starRating?: number | null;
    userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
    facilities: Array<{ __typename?: 'AccommodationCatalogFacilityOrAmenity'; label: string }>;
    photos: Array<{ __typename?: 'AccommodationCatalogPhoto'; url: string }>;
    location: { __typename?: 'AccommodationCatalogLocation'; latitude?: number | null; longitude?: number | null };
    address?: {
      __typename?: 'AccommodationCatalogPropertyAddress';
      city?: string | null;
      country?: string | null;
      postalCode?: string | null;
      state?: string | null;
      street?: string | null;
    } | null;
  };
};

export type PropertiesWithAccommodationPlaceQueryVariables = Types.Exact<{
  idObject: Types.AccommodationCatalogPropertyIdentifierInput;
  accommodationPlaceId: Types.Scalars['ID'];
  pageContainerBySlugPayload2: Types.PageContainerBySlugPayload;
}>;

export type PropertiesWithAccommodationPlaceQuery = {
  __typename?: 'Query';
  accommodationCatalogPropertyById: {
    __typename: 'AccommodationCatalogProperty';
    joyPlaceId: string;
    name: string;
    plainTextLongDescription: string;
    starRating?: number | null;
    userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
    facilities: Array<{ __typename?: 'AccommodationCatalogFacilityOrAmenity'; label: string }>;
    photos: Array<{ __typename?: 'AccommodationCatalogPhoto'; url: string }>;
    location: { __typename?: 'AccommodationCatalogLocation'; latitude?: number | null; longitude?: number | null };
    address?: {
      __typename?: 'AccommodationCatalogPropertyAddress';
      city?: string | null;
      country?: string | null;
      postalCode?: string | null;
      state?: string | null;
      street?: string | null;
    } | null;
  };
  accommodationPlaceV2ById?: {
    __typename?: 'AccommodationPlaceV2';
    id: string;
    isPhotoHidden?: boolean | null;
    name?: string | null;
    customName?: string | null;
    userNote?: string | null;
    description?: string | null;
    lat?: number | null;
    long?: number | null;
    city?: string | null;
    country?: string | null;
    postalCode?: string | null;
    state?: string | null;
    address?: string | null;
    photo?: { __typename?: 'Photo'; id: string; url: string; altTag?: string | null } | null;
    customPhoto?: { __typename?: 'Photo'; id: string; altTag?: string | null; url: string } | null;
  } | null;
  pageContainerBySlug?: {
    __typename?: 'PageContainer';
    id: string;
    components?: Array<
      | { __typename?: 'EventCountdownComponent' }
      | { __typename?: 'EventDetailsComponent' }
      | { __typename?: 'EventDisplayNameComponent' }
      | { __typename?: 'GreetingMessageComponent' }
      | { __typename?: 'JsonDataBlobComponent' }
      | { __typename?: 'MediaGalleryComponent' }
      | { __typename?: 'PhotoComponent' }
      | { __typename?: 'PlacesMapComponent' }
      | { __typename?: 'RsvpAndUnlockGuestDetailsButtonComponent' }
      | { __typename?: 'TextComponent'; id: string }
      | { __typename?: 'TravelMapComponent' }
      | { __typename?: 'VideoComponent' }
    > | null;
  } | null;
};

export type TravelOffersForAccommodationPropertiesQueryVariables = Types.Exact<{
  payload: Types.TravelOffersForAccommodationForPropertiesPayload;
  eventId: Types.Scalars['ID'];
  includeWalletSpend?: Types.Scalars['Boolean'];
  includeRateLockEligibility?: Types.Scalars['Boolean'];
}>;

export type TravelOffersForAccommodationPropertiesQuery = {
  __typename?: 'Query';
  travelOffersForAccommodationProperties: {
    __typename?: 'TravelOffersResult';
    someOffers: Array<{
      __typename: 'TravelOffer';
      _diag?: any | null;
      offerToken: string;
      checkIn: string;
      checkOut: string;
      offerGroupId?: string | null;
      providerName?: string | null;
      availability?: number | null;
      rateLockEligibility?: { __typename?: 'RateLockEligibility'; eligible: boolean } | null;
      walletSpend?: {
        __typename?: 'TravelOfferWalletSpend';
        coverage: Types.TravelOfferWalletSpendCoverage;
        partialAllowed: boolean;
        fundAppliedMinorUnits: number;
        cardDueNowMinorUnits: number;
        fundBalanceMinorUnits: number;
        currency: string;
        suggestedCoupon?: {
          __typename?: 'TravelOfferWalletSpendSuggestedCoupon';
          code: string;
          discountType: Types.DiscountTypeDtoEnum;
          discountValue: number;
          discountAmountMinorUnits: number;
          couponPaidWith: Types.CouponPaidWithDtoEnum;
          discountBaseMinorUnits?: number | null;
        } | null;
      } | null;
      components: Array<{
        __typename: 'TravelOfferAccommodationUnitsComponent';
        distributionType?: Types.TravelOfferDistributionType | null;
        componentType: string;
        refundability: Types.TravelOfferRefundability;
        policies: Array<{ __typename?: 'TravelOfferPolicyField'; title: string; description: string }>;
        cancellationPolicies?: Array<{
          __typename?: 'TravelOfferCancellationPolicyRule';
          end?: string | null;
          start: string;
          estimatedCancellationAmount?: {
            __typename?: 'TravelOfferEstimatedCancellationAmount';
            valueType: Types.TravelOfferCancellationPolicyRuleValueType;
            amount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
          } | null;
          estimatedFeeAmount?: {
            __typename?: 'TravelMonetaryValue';
            currency: Types.TravelCurrencyCode;
            digits: number;
            floatingPointDecimalString: string;
            valueInMinorUnits: number;
          } | null;
        }> | null;
        boardBasis?: { __typename?: 'AccommodationCatalogFacilityOrAmenity'; label: string } | null;
        occupancies: Array<{ __typename?: 'TravelOfferAccommodationUnitsComponentOccupancy'; adults: number; children: number; childrenAges?: Array<number> | null }>;
        unit: {
          __typename: 'AccommodationCatalogPropertyUnit';
          id: string;
          name: string;
          plainTextLongDescription: string;
          facilities: Array<{ __typename: 'AccommodationCatalogFacilityOrAmenity'; label: string }>;
          photos: Array<{ __typename?: 'AccommodationCatalogPhoto'; url: string }>;
          property: {
            __typename?: 'AccommodationCatalogProperty';
            name: string;
            joyPlaceId: string;
            starRating?: number | null;
            address?: {
              __typename?: 'AccommodationCatalogPropertyAddress';
              city?: string | null;
              state?: string | null;
              street?: string | null;
              country?: string | null;
              postalCode?: string | null;
            } | null;
            photos: Array<{
              __typename?: 'AccommodationCatalogPhoto';
              url: string;
              subject: { __typename?: 'AccommodationCatalogPhotoSubject'; label: string; type: Types.AccommodationCatalogPhotoSubjectType };
            }>;
            userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
          };
        };
      }>;
      onsiteCharges: Array<{
        __typename?: 'TravelOfferOnsiteCharge';
        title: string;
        units?: Array<string> | null;
        unitPrice: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
      }>;
      fees: Array<{
        __typename?: 'TravelOfferFee';
        isIncludedInTotal: boolean;
        rate: number;
        type: Types.FeeType;
        visibility: string;
        amount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnitsBigInt: any };
      }>;
      baseAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
      totalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
      allInTotalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
      onsiteChargesTotalAmount?: {
        __typename?: 'TravelMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      } | null;
      originalBaseAmount?: {
        __typename?: 'TravelMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      } | null;
      originalTotalAmount?: {
        __typename?: 'TravelMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      } | null;
      prebookChange?: {
        __typename?: 'TravelOfferPrebookChange';
        priceDifferencePercent: number;
        cancellationChanged: boolean;
        boardChanged: boolean;
        oldAmount?: {
          __typename?: 'TravelMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        } | null;
        newAmount?: {
          __typename?: 'TravelMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        } | null;
      } | null;
    }>;
  };
};

export type PricingsForAccommodationByRegionQueryVariables = Types.Exact<{
  payload: Types.TravelPricingsForAccommodationByRegionPayload;
}>;

export type PricingsForAccommodationByRegionQuery = {
  __typename?: 'Query';
  pricingsForAccommodationByRegion: {
    __typename?: 'TravelPricingsResult';
    pricings: Array<{
      __typename?: 'TravelPricing';
      property: {
        __typename?: 'AccommodationCatalogProperty';
        joyPlaceId: string;
        relevanceScore?: number | null;
        starRating?: number | null;
        name: string;
        plainTextLongDescription: string;
        userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
        location: { __typename?: 'AccommodationCatalogLocation'; latitude?: number | null; longitude?: number | null; timezone?: string | null };
        photos: Array<{
          __typename?: 'AccommodationCatalogPhoto';
          url: string;
          subject: { __typename?: 'AccommodationCatalogPhotoSubject'; label: string; type: Types.AccommodationCatalogPhotoSubjectType };
        }>;
      };
      totalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
    }>;
  };
};

export type ServiceCenterAccommodationPlaceFragment = {
  __typename?: 'AccommodationPlaceV2';
  id: string;
  type?: Types.AccommodationPlaceType | null;
  zhId?: string | null;
  joyPlaceId?: string | null;
  googlePlaceId?: string | null;
};

export type ServiceCenterAddAccommodationPlaceV2MutationVariables = Types.Exact<{
  payload: Types.AddAccommodationPlaceV2Request;
}>;

export type ServiceCenterAddAccommodationPlaceV2Mutation = {
  __typename?: 'Mutation';
  addAccommodationPlaceV2: {
    __typename?: 'AccommodationPlaceV2';
    id: string;
    type?: Types.AccommodationPlaceType | null;
    zhId?: string | null;
    joyPlaceId?: string | null;
    googlePlaceId?: string | null;
  };
};

export type ServiceCenterDeleteAccommodationPlaceV2MutationVariables = Types.Exact<{
  id: Types.Scalars['String'];
}>;

export type ServiceCenterDeleteAccommodationPlaceV2Mutation = {
  __typename?: 'Mutation';
  deleteAccommodationPlaceV2: {
    __typename?: 'AccommodationPlaceV2';
    id: string;
    type?: Types.AccommodationPlaceType | null;
    zhId?: string | null;
    joyPlaceId?: string | null;
    googlePlaceId?: string | null;
  };
};

export type TravelOfferReservationUpdateMutationVariables = Types.Exact<{
  payload: Types.TravelOfferReservationUpdatePayload;
}>;

export type TravelOfferReservationUpdateMutation = {
  __typename?: 'Mutation';
  travelOfferReservationUpdate: { __typename?: 'TravelOfferReservation'; id: string; status: Types.TravelOfferReservationStatus };
};

export type ServiceCenterAccommodationPlacesQueryVariables = Types.Exact<{
  name: Types.Scalars['String'];
}>;

export type ServiceCenterAccommodationPlacesQuery = {
  __typename?: 'Query';
  eventByName?: {
    __typename?: 'Event';
    id: string;
    accommodationPlacesV2: Array<{
      __typename?: 'AccommodationPlaceV2';
      id: string;
      type?: Types.AccommodationPlaceType | null;
      zhId?: string | null;
      joyPlaceId?: string | null;
      googlePlaceId?: string | null;
    }>;
  } | null;
};

export type TravelOfferFromOfferTokenQueryVariables = Types.Exact<{
  payload: Types.Scalars['String'];
  params?: Types.InputMaybe<Types.TravelOfferFromOfferTokenParams>;
  useCache?: Types.InputMaybe<Types.Scalars['Boolean']>;
}>;

export type TravelOfferFromOfferTokenQuery = {
  __typename?: 'Query';
  travelOfferFromOfferToken: {
    __typename: 'TravelOffer';
    offerToken: string;
    checkIn: string;
    checkOut: string;
    offerGroupId?: string | null;
    providerName?: string | null;
    availability?: number | null;
    rateLockEligibility?: { __typename?: 'RateLockEligibility'; eligible: boolean; freeCancellationUntil?: string | null } | null;
    components: Array<{
      __typename: 'TravelOfferAccommodationUnitsComponent';
      distributionType?: Types.TravelOfferDistributionType | null;
      componentType: string;
      refundability: Types.TravelOfferRefundability;
      policies: Array<{ __typename?: 'TravelOfferPolicyField'; title: string; description: string }>;
      cancellationPolicies?: Array<{
        __typename?: 'TravelOfferCancellationPolicyRule';
        end?: string | null;
        start: string;
        estimatedCancellationAmount?: {
          __typename?: 'TravelOfferEstimatedCancellationAmount';
          valueType: Types.TravelOfferCancellationPolicyRuleValueType;
          amount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
        } | null;
        estimatedFeeAmount?: {
          __typename?: 'TravelMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        } | null;
      }> | null;
      boardBasis?: { __typename?: 'AccommodationCatalogFacilityOrAmenity'; label: string } | null;
      occupancies: Array<{ __typename?: 'TravelOfferAccommodationUnitsComponentOccupancy'; adults: number; children: number; childrenAges?: Array<number> | null }>;
      unit: {
        __typename: 'AccommodationCatalogPropertyUnit';
        id: string;
        name: string;
        plainTextLongDescription: string;
        facilities: Array<{ __typename: 'AccommodationCatalogFacilityOrAmenity'; label: string }>;
        photos: Array<{ __typename?: 'AccommodationCatalogPhoto'; url: string }>;
        property: {
          __typename?: 'AccommodationCatalogProperty';
          name: string;
          joyPlaceId: string;
          starRating?: number | null;
          address?: {
            __typename?: 'AccommodationCatalogPropertyAddress';
            city?: string | null;
            state?: string | null;
            street?: string | null;
            country?: string | null;
            postalCode?: string | null;
          } | null;
          photos: Array<{
            __typename?: 'AccommodationCatalogPhoto';
            url: string;
            subject: { __typename?: 'AccommodationCatalogPhotoSubject'; label: string; type: Types.AccommodationCatalogPhotoSubjectType };
          }>;
          userRating?: { __typename?: 'UserRating'; ratingCount?: number | null; ratingValue?: number | null } | null;
        };
      };
    }>;
    onsiteCharges: Array<{
      __typename?: 'TravelOfferOnsiteCharge';
      title: string;
      units?: Array<string> | null;
      unitPrice: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
    }>;
    fees: Array<{
      __typename?: 'TravelOfferFee';
      isIncludedInTotal: boolean;
      rate: number;
      type: Types.FeeType;
      visibility: string;
      amount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnitsBigInt: any };
    }>;
    baseAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
    totalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
    allInTotalAmount: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number };
    onsiteChargesTotalAmount?: {
      __typename?: 'TravelMonetaryValue';
      currency: Types.TravelCurrencyCode;
      digits: number;
      floatingPointDecimalString: string;
      valueInMinorUnits: number;
    } | null;
    originalBaseAmount?: {
      __typename?: 'TravelMonetaryValue';
      currency: Types.TravelCurrencyCode;
      digits: number;
      floatingPointDecimalString: string;
      valueInMinorUnits: number;
    } | null;
    originalTotalAmount?: {
      __typename?: 'TravelMonetaryValue';
      currency: Types.TravelCurrencyCode;
      digits: number;
      floatingPointDecimalString: string;
      valueInMinorUnits: number;
    } | null;
    prebookChange?: {
      __typename?: 'TravelOfferPrebookChange';
      priceDifferencePercent: number;
      cancellationChanged: boolean;
      boardChanged: boolean;
      oldAmount?: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number } | null;
      newAmount?: { __typename?: 'TravelMonetaryValue'; currency: Types.TravelCurrencyCode; digits: number; floatingPointDecimalString: string; valueInMinorUnits: number } | null;
    } | null;
  };
};

export type TravelOfferReservationCreateMutationVariables = Types.Exact<{
  payload: Types.TravelOfferReservationCreatePayload;
}>;

export type TravelOfferReservationCreateMutation = {
  __typename?: 'Mutation';
  travelOfferReservationCreate:
    | {
        __typename?: 'TravelOfferReservationCreateStripeNativeResult';
        paymentIntentClientSecret: string;
        reservation: {
          __typename?: 'TravelOfferReservation';
          id: string;
          offerToken: string;
          status: Types.TravelOfferReservationStatus;
          joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
          joyDiscountCoupon?: {
            __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
            couponCode: string;
            discountAmount: number;
            discountCurrency: string;
            discountedBaseCostMinorUnits?: number | null;
            discountedTaxesAndFeesMinorUnits?: number | null;
            isEmailRestricted: boolean;
            scopeType: Types.ScopeTypeDtoEnum;
            bookerType: Types.BookerTypeDtoEnum;
            allowedPaidWith?: Types.CouponPaidWithDtoEnum | null;
            discountBaseMinorUnits?: number | null;
          } | null;
          confirmation?: {
            __typename?: 'TravelOfferReservationConfirmation';
            billingContact: {
              __typename?: 'TravelOfferReservationContactInfo';
              email: string;
              firstName: string;
              lastName: string;
              middleName?: string | null;
              phone: string;
              title: string;
              address: {
                __typename?: 'TravelReservationAddress';
                city: string;
                country: string;
                line1: string;
                line2?: string | null;
                postalCode?: string | null;
                state?: string | null;
              };
            };
          } | null;
          roomGuests: Array<{
            __typename?: 'TravelOfferReservationRoomGuests';
            unitId: string;
            guests: Array<{
              __typename?: 'TravelOfferReservationGuestInformation';
              ageClassification: Types.TravelReservationGuestAgeClassification;
              firstName: string;
              lastName: string;
              title?: Types.TravelReservationGuestTitle | null;
            }>;
          }>;
        };
      }
    | {
        __typename?: 'TravelOfferReservationCreateTravelWalletResult';
        outcome: Types.TravelOfferReservationCreateTravelWalletOutcome;
        failureCode?: Types.TravelOfferReservationCreateTravelWalletFailureCode | null;
        failureReason?: string | null;
        walletReservation?: {
          __typename?: 'TravelOfferReservation';
          id: string;
          offerToken: string;
          status: Types.TravelOfferReservationStatus;
          joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
          joyDiscountCoupon?: {
            __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
            couponCode: string;
            discountAmount: number;
            discountCurrency: string;
            discountedBaseCostMinorUnits?: number | null;
            discountedTaxesAndFeesMinorUnits?: number | null;
            isEmailRestricted: boolean;
            scopeType: Types.ScopeTypeDtoEnum;
            bookerType: Types.BookerTypeDtoEnum;
            allowedPaidWith?: Types.CouponPaidWithDtoEnum | null;
            discountBaseMinorUnits?: number | null;
          } | null;
          confirmation?: {
            __typename?: 'TravelOfferReservationConfirmation';
            billingContact: {
              __typename?: 'TravelOfferReservationContactInfo';
              email: string;
              firstName: string;
              lastName: string;
              middleName?: string | null;
              phone: string;
              title: string;
              address: {
                __typename?: 'TravelReservationAddress';
                city: string;
                country: string;
                line1: string;
                line2?: string | null;
                postalCode?: string | null;
                state?: string | null;
              };
            };
          } | null;
          roomGuests: Array<{
            __typename?: 'TravelOfferReservationRoomGuests';
            unitId: string;
            guests: Array<{
              __typename?: 'TravelOfferReservationGuestInformation';
              ageClassification: Types.TravelReservationGuestAgeClassification;
              firstName: string;
              lastName: string;
              title?: Types.TravelReservationGuestTitle | null;
            }>;
          }>;
        } | null;
      }
    | {
        __typename?: 'TravelOfferReservationCreateVaulteraVaultResult';
        vaulteraToken?: string | null;
        reservation: {
          __typename?: 'TravelOfferReservation';
          id: string;
          offerToken: string;
          status: Types.TravelOfferReservationStatus;
          joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
          joyDiscountCoupon?: {
            __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
            couponCode: string;
            discountAmount: number;
            discountCurrency: string;
            discountedBaseCostMinorUnits?: number | null;
            discountedTaxesAndFeesMinorUnits?: number | null;
            isEmailRestricted: boolean;
            scopeType: Types.ScopeTypeDtoEnum;
            bookerType: Types.BookerTypeDtoEnum;
            allowedPaidWith?: Types.CouponPaidWithDtoEnum | null;
            discountBaseMinorUnits?: number | null;
          } | null;
          confirmation?: {
            __typename?: 'TravelOfferReservationConfirmation';
            billingContact: {
              __typename?: 'TravelOfferReservationContactInfo';
              email: string;
              firstName: string;
              lastName: string;
              middleName?: string | null;
              phone: string;
              title: string;
              address: {
                __typename?: 'TravelReservationAddress';
                city: string;
                country: string;
                line1: string;
                line2?: string | null;
                postalCode?: string | null;
                state?: string | null;
              };
            };
          } | null;
          roomGuests: Array<{
            __typename?: 'TravelOfferReservationRoomGuests';
            unitId: string;
            guests: Array<{
              __typename?: 'TravelOfferReservationGuestInformation';
              ageClassification: Types.TravelReservationGuestAgeClassification;
              firstName: string;
              lastName: string;
              title?: Types.TravelReservationGuestTitle | null;
            }>;
          }>;
        };
      };
};

export type TravelOfferReservationConfirmMutationVariables = Types.Exact<{
  payload: Types.TravelOfferReservationConfirmPayload;
}>;

export type TravelOfferReservationConfirmMutation = {
  __typename?: 'Mutation';
  travelOfferReservationConfirm:
    | { __typename?: 'TravelOfferReservationConfirmFailure'; errorMessage: string }
    | {
        __typename?: 'TravelOfferReservationConfirmSuccess';
        reservation: { __typename?: 'TravelOfferReservation'; id: string; offerToken: string; status: Types.TravelOfferReservationStatus };
      };
};

export type TravelOfferReservationFullDetailsFragment = {
  __typename?: 'TravelOfferReservation';
  id: string;
  offerToken: string;
  email?: string | null;
  status: Types.TravelOfferReservationStatus;
  cardPaidMinorUnits?: number | null;
  roomGuests: Array<{
    __typename?: 'TravelOfferReservationRoomGuests';
    unitId: string;
    guests: Array<{
      __typename?: 'TravelOfferReservationGuestInformation';
      age?: number | null;
      ageClassification: Types.TravelReservationGuestAgeClassification;
      firstName: string;
      lastName: string;
      title?: Types.TravelReservationGuestTitle | null;
      phoneNumber?: string | null;
    }>;
  }>;
  cancellationFees?: {
    __typename?: 'TravelReservationMonetaryValue';
    currency: Types.TravelCurrencyCode;
    valueInMinorUnits: number;
    floatingPointDecimalString: string;
    digits: number;
  } | null;
  paymentMethodSummary?: {
    __typename?: 'TravelOfferReservationPaymentMethodSummary';
    type: string;
    cardBrand?: string | null;
    cardLast4?: string | null;
    walletType?: string | null;
    statementDescriptor?: string | null;
  } | null;
  travelWallet?: {
    __typename?: 'TravelReservationWalletSummary';
    appliedMinorUnits?: number | null;
    balanceMinorUnits?: number | null;
    refundedMinorUnits?: number | null;
    currency?: string | null;
  } | null;
  joyDiscountCoupon?: {
    __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
    couponCode: string;
    discountAmount: number;
    discountCurrency: string;
    discountedBaseCostMinorUnits?: number | null;
    discountedTaxesAndFeesMinorUnits?: number | null;
    isEmailRestricted: boolean;
    scopeType: Types.ScopeTypeDtoEnum;
    bookerType: Types.BookerTypeDtoEnum;
    discountBaseMinorUnits?: number | null;
  } | null;
  joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
  confirmation?: {
    __typename?: 'TravelOfferReservationConfirmation';
    provider?: string | null;
    otaProvider?: Types.OTAProvider | null;
    propertyConfirmationCode?: string | null;
    checkIn: string;
    checkOut: string;
    externalOrderId?: string | null;
    baseAmount: {
      __typename?: 'TravelReservationMonetaryValue';
      currency: Types.TravelCurrencyCode;
      digits: number;
      floatingPointDecimalString: string;
      valueInMinorUnits: number;
    };
    billingContact: {
      __typename?: 'TravelOfferReservationContactInfo';
      email: string;
      firstName: string;
      lastName: string;
      middleName?: string | null;
      phone: string;
      title: string;
      address: { __typename?: 'TravelReservationAddress'; city: string; country: string; line1: string; line2?: string | null; postalCode?: string | null; state?: string | null };
    };
    onsiteCharges: Array<{
      __typename?: 'TravelOfferReservationOnsiteCharge';
      title: string;
      units?: Array<string> | null;
      unitPrice: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      };
    }>;
    propertyInfo: {
      __typename?: 'TravelOfferReservationConfirmationPropertyInfo';
      checkInTime?: string | null;
      checkOutTime?: string | null;
      checkInInstructions?: string | null;
      checkInSpecialInstructions?: string | null;
      heroImageUrl: string;
      name: string;
      address: { __typename?: 'TravelReservationAddress'; city: string; country: string; line1: string; line2?: string | null; postalCode?: string | null; state?: string | null };
      policies?: Array<{ __typename?: 'OfferPolicy'; title?: string | null; description?: string | null }> | null;
      cancellationPolicies?: Array<{
        __typename?: 'TravelOfferReservationCancellationPolicyRule';
        start: string;
        end?: string | null;
        estimatedCancellationAmount?: {
          __typename?: 'TravelOfferReservationEstimatedCancellationAmount';
          amount: {
            __typename?: 'TravelReservationMonetaryValue';
            currency: Types.TravelCurrencyCode;
            digits: number;
            floatingPointDecimalString: string;
            valueInMinorUnits: number;
          };
        } | null;
        estimatedFeeAmount?: {
          __typename?: 'TravelReservationMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        } | null;
      }> | null;
    };
    reservationItems: Array<{
      __typename?: 'TravelOfferReservationItem';
      refundability: Types.TravelOfferReservationRefundability;
      occupancy: { __typename?: 'TravelOfferReservationItemOccupancy'; adults: number; children: number };
      boardBasis?: { __typename?: 'TravelOfferReservationFacility'; label: string } | null;
      unit: {
        __typename?: 'TravelOfferReservationUnit';
        id: string;
        name: string;
        description?: string | null;
        facilities?: Array<{ __typename?: 'TravelOfferReservationFacility'; label: string }> | null;
      };
    }>;
    totalAmount: {
      __typename?: 'TravelReservationMonetaryValue';
      currency: Types.TravelCurrencyCode;
      digits: number;
      floatingPointDecimalString: string;
      valueInMinorUnits: number;
    };
    discountAmount?: {
      __typename?: 'TravelReservationMonetaryValue';
      currency: Types.TravelCurrencyCode;
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      digits: number;
    } | null;
    fees: Array<{
      __typename?: 'TravelOfferReservationFee';
      isIncludedInTotal: boolean;
      rate: number;
      type: string;
      visibility: string;
      amount: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnitsBigInt: any;
      };
    }>;
  } | null;
};

export type TravelOfferReservationStatusDetailsFragment = {
  __typename?: 'TravelOfferReservation';
  id: string;
  offerToken: string;
  status: Types.TravelOfferReservationStatus;
  joyPlaceId?: string | null;
  eventId?: string | null;
  isRateLock?: boolean | null;
  rateLockStatus?: Types.RateLockStatus | null;
  lockExpiresAt?: any | null;
  rateLockResolvedAt?: any | null;
};

export type TravelOfferReservationByIdQueryVariables = Types.Exact<{
  travelOfferReservationByIdId: Types.Scalars['String'];
}>;

export type TravelOfferReservationByIdQuery = {
  __typename?: 'Query';
  travelOfferReservationById?: {
    __typename?: 'TravelOfferReservation';
    id: string;
    offerToken: string;
    email?: string | null;
    status: Types.TravelOfferReservationStatus;
    cardPaidMinorUnits?: number | null;
    roomGuests: Array<{
      __typename?: 'TravelOfferReservationRoomGuests';
      unitId: string;
      guests: Array<{
        __typename?: 'TravelOfferReservationGuestInformation';
        age?: number | null;
        ageClassification: Types.TravelReservationGuestAgeClassification;
        firstName: string;
        lastName: string;
        title?: Types.TravelReservationGuestTitle | null;
        phoneNumber?: string | null;
      }>;
    }>;
    cancellationFees?: {
      __typename?: 'TravelReservationMonetaryValue';
      currency: Types.TravelCurrencyCode;
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      digits: number;
    } | null;
    paymentMethodSummary?: {
      __typename?: 'TravelOfferReservationPaymentMethodSummary';
      type: string;
      cardBrand?: string | null;
      cardLast4?: string | null;
      walletType?: string | null;
      statementDescriptor?: string | null;
    } | null;
    travelWallet?: {
      __typename?: 'TravelReservationWalletSummary';
      appliedMinorUnits?: number | null;
      balanceMinorUnits?: number | null;
      refundedMinorUnits?: number | null;
      currency?: string | null;
    } | null;
    joyDiscountCoupon?: {
      __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
      couponCode: string;
      discountAmount: number;
      discountCurrency: string;
      discountedBaseCostMinorUnits?: number | null;
      discountedTaxesAndFeesMinorUnits?: number | null;
      isEmailRestricted: boolean;
      scopeType: Types.ScopeTypeDtoEnum;
      bookerType: Types.BookerTypeDtoEnum;
      discountBaseMinorUnits?: number | null;
    } | null;
    joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
    confirmation?: {
      __typename?: 'TravelOfferReservationConfirmation';
      provider?: string | null;
      otaProvider?: Types.OTAProvider | null;
      propertyConfirmationCode?: string | null;
      checkIn: string;
      checkOut: string;
      externalOrderId?: string | null;
      baseAmount: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      };
      billingContact: {
        __typename?: 'TravelOfferReservationContactInfo';
        email: string;
        firstName: string;
        lastName: string;
        middleName?: string | null;
        phone: string;
        title: string;
        address: {
          __typename?: 'TravelReservationAddress';
          city: string;
          country: string;
          line1: string;
          line2?: string | null;
          postalCode?: string | null;
          state?: string | null;
        };
      };
      onsiteCharges: Array<{
        __typename?: 'TravelOfferReservationOnsiteCharge';
        title: string;
        units?: Array<string> | null;
        unitPrice: {
          __typename?: 'TravelReservationMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        };
      }>;
      propertyInfo: {
        __typename?: 'TravelOfferReservationConfirmationPropertyInfo';
        checkInTime?: string | null;
        checkOutTime?: string | null;
        checkInInstructions?: string | null;
        checkInSpecialInstructions?: string | null;
        heroImageUrl: string;
        name: string;
        address: {
          __typename?: 'TravelReservationAddress';
          city: string;
          country: string;
          line1: string;
          line2?: string | null;
          postalCode?: string | null;
          state?: string | null;
        };
        policies?: Array<{ __typename?: 'OfferPolicy'; title?: string | null; description?: string | null }> | null;
        cancellationPolicies?: Array<{
          __typename?: 'TravelOfferReservationCancellationPolicyRule';
          start: string;
          end?: string | null;
          estimatedCancellationAmount?: {
            __typename?: 'TravelOfferReservationEstimatedCancellationAmount';
            amount: {
              __typename?: 'TravelReservationMonetaryValue';
              currency: Types.TravelCurrencyCode;
              digits: number;
              floatingPointDecimalString: string;
              valueInMinorUnits: number;
            };
          } | null;
          estimatedFeeAmount?: {
            __typename?: 'TravelReservationMonetaryValue';
            currency: Types.TravelCurrencyCode;
            digits: number;
            floatingPointDecimalString: string;
            valueInMinorUnits: number;
          } | null;
        }> | null;
      };
      reservationItems: Array<{
        __typename?: 'TravelOfferReservationItem';
        refundability: Types.TravelOfferReservationRefundability;
        occupancy: { __typename?: 'TravelOfferReservationItemOccupancy'; adults: number; children: number };
        boardBasis?: { __typename?: 'TravelOfferReservationFacility'; label: string } | null;
        unit: {
          __typename?: 'TravelOfferReservationUnit';
          id: string;
          name: string;
          description?: string | null;
          facilities?: Array<{ __typename?: 'TravelOfferReservationFacility'; label: string }> | null;
        };
      }>;
      totalAmount: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      };
      discountAmount?: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        digits: number;
      } | null;
      fees: Array<{
        __typename?: 'TravelOfferReservationFee';
        isIncludedInTotal: boolean;
        rate: number;
        type: string;
        visibility: string;
        amount: {
          __typename?: 'TravelReservationMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnitsBigInt: any;
        };
      }>;
    } | null;
  } | null;
};

export type TravelOfferReservationByIdOrOrderIdQueryVariables = Types.Exact<{
  travelOfferReservationByIdOrOrderIdId: Types.Scalars['String'];
}>;

export type TravelOfferReservationByIdOrOrderIdQuery = {
  __typename?: 'Query';
  travelOfferReservationByIdOrOrderId?: {
    __typename?: 'TravelOfferReservation';
    id: string;
    offerToken: string;
    email?: string | null;
    status: Types.TravelOfferReservationStatus;
    cardPaidMinorUnits?: number | null;
    roomGuests: Array<{
      __typename?: 'TravelOfferReservationRoomGuests';
      unitId: string;
      guests: Array<{
        __typename?: 'TravelOfferReservationGuestInformation';
        age?: number | null;
        ageClassification: Types.TravelReservationGuestAgeClassification;
        firstName: string;
        lastName: string;
        title?: Types.TravelReservationGuestTitle | null;
        phoneNumber?: string | null;
      }>;
    }>;
    cancellationFees?: {
      __typename?: 'TravelReservationMonetaryValue';
      currency: Types.TravelCurrencyCode;
      valueInMinorUnits: number;
      floatingPointDecimalString: string;
      digits: number;
    } | null;
    paymentMethodSummary?: {
      __typename?: 'TravelOfferReservationPaymentMethodSummary';
      type: string;
      cardBrand?: string | null;
      cardLast4?: string | null;
      walletType?: string | null;
      statementDescriptor?: string | null;
    } | null;
    travelWallet?: {
      __typename?: 'TravelReservationWalletSummary';
      appliedMinorUnits?: number | null;
      balanceMinorUnits?: number | null;
      refundedMinorUnits?: number | null;
      currency?: string | null;
    } | null;
    joyDiscountCoupon?: {
      __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
      couponCode: string;
      discountAmount: number;
      discountCurrency: string;
      discountedBaseCostMinorUnits?: number | null;
      discountedTaxesAndFeesMinorUnits?: number | null;
      isEmailRestricted: boolean;
      scopeType: Types.ScopeTypeDtoEnum;
      bookerType: Types.BookerTypeDtoEnum;
      discountBaseMinorUnits?: number | null;
    } | null;
    joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
    confirmation?: {
      __typename?: 'TravelOfferReservationConfirmation';
      provider?: string | null;
      otaProvider?: Types.OTAProvider | null;
      propertyConfirmationCode?: string | null;
      checkIn: string;
      checkOut: string;
      externalOrderId?: string | null;
      baseAmount: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      };
      billingContact: {
        __typename?: 'TravelOfferReservationContactInfo';
        email: string;
        firstName: string;
        lastName: string;
        middleName?: string | null;
        phone: string;
        title: string;
        address: {
          __typename?: 'TravelReservationAddress';
          city: string;
          country: string;
          line1: string;
          line2?: string | null;
          postalCode?: string | null;
          state?: string | null;
        };
      };
      onsiteCharges: Array<{
        __typename?: 'TravelOfferReservationOnsiteCharge';
        title: string;
        units?: Array<string> | null;
        unitPrice: {
          __typename?: 'TravelReservationMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnits: number;
        };
      }>;
      propertyInfo: {
        __typename?: 'TravelOfferReservationConfirmationPropertyInfo';
        checkInTime?: string | null;
        checkOutTime?: string | null;
        checkInInstructions?: string | null;
        checkInSpecialInstructions?: string | null;
        heroImageUrl: string;
        name: string;
        address: {
          __typename?: 'TravelReservationAddress';
          city: string;
          country: string;
          line1: string;
          line2?: string | null;
          postalCode?: string | null;
          state?: string | null;
        };
        policies?: Array<{ __typename?: 'OfferPolicy'; title?: string | null; description?: string | null }> | null;
        cancellationPolicies?: Array<{
          __typename?: 'TravelOfferReservationCancellationPolicyRule';
          start: string;
          end?: string | null;
          estimatedCancellationAmount?: {
            __typename?: 'TravelOfferReservationEstimatedCancellationAmount';
            amount: {
              __typename?: 'TravelReservationMonetaryValue';
              currency: Types.TravelCurrencyCode;
              digits: number;
              floatingPointDecimalString: string;
              valueInMinorUnits: number;
            };
          } | null;
          estimatedFeeAmount?: {
            __typename?: 'TravelReservationMonetaryValue';
            currency: Types.TravelCurrencyCode;
            digits: number;
            floatingPointDecimalString: string;
            valueInMinorUnits: number;
          } | null;
        }> | null;
      };
      reservationItems: Array<{
        __typename?: 'TravelOfferReservationItem';
        refundability: Types.TravelOfferReservationRefundability;
        occupancy: { __typename?: 'TravelOfferReservationItemOccupancy'; adults: number; children: number };
        boardBasis?: { __typename?: 'TravelOfferReservationFacility'; label: string } | null;
        unit: {
          __typename?: 'TravelOfferReservationUnit';
          id: string;
          name: string;
          description?: string | null;
          facilities?: Array<{ __typename?: 'TravelOfferReservationFacility'; label: string }> | null;
        };
      }>;
      totalAmount: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        digits: number;
        floatingPointDecimalString: string;
        valueInMinorUnits: number;
      };
      discountAmount?: {
        __typename?: 'TravelReservationMonetaryValue';
        currency: Types.TravelCurrencyCode;
        valueInMinorUnits: number;
        floatingPointDecimalString: string;
        digits: number;
      } | null;
      fees: Array<{
        __typename?: 'TravelOfferReservationFee';
        isIncludedInTotal: boolean;
        rate: number;
        type: string;
        visibility: string;
        amount: {
          __typename?: 'TravelReservationMonetaryValue';
          currency: Types.TravelCurrencyCode;
          digits: number;
          floatingPointDecimalString: string;
          valueInMinorUnitsBigInt: any;
        };
      }>;
    } | null;
  } | null;
};

export type TravelOfferReservationStatusByIdOrOrderIdQueryVariables = Types.Exact<{
  travelOfferReservationStatusByIdOrOrderId: Types.Scalars['String'];
}>;

export type TravelOfferReservationStatusByIdOrOrderIdQuery = {
  __typename?: 'Query';
  travelOfferReservationByIdOrOrderId?: {
    __typename?: 'TravelOfferReservation';
    id: string;
    offerToken: string;
    status: Types.TravelOfferReservationStatus;
    joyPlaceId?: string | null;
    eventId?: string | null;
    isRateLock?: boolean | null;
    rateLockStatus?: Types.RateLockStatus | null;
    lockExpiresAt?: any | null;
    rateLockResolvedAt?: any | null;
  } | null;
};

export type GetEventByIdForReservationStatusPageQueryVariables = Types.Exact<{
  eventId: Types.Scalars['ID'];
}>;

export type GetEventByIdForReservationStatusPageQuery = {
  __typename?: 'Query';
  eventById?: {
    __typename?: 'Event';
    id: string;
    website: string;
    travelSettings?: { __typename?: 'TravelSettings'; checkInDate?: string | null; checkOutDate?: string | null } | null;
    info: { __typename?: 'EventInfo'; id: string; eventDisplayName?: string | null };
  } | null;
};

export type CurrentEventGuestQueryVariables = Types.Exact<{
  eventId: Types.Scalars['ID'];
}>;

export type CurrentEventGuestQuery = {
  __typename?: 'Query';
  eventById?: {
    __typename?: 'Event';
    id: string;
    activeSession: {
      __typename?: 'EventSession';
      id: string;
      person?: { __typename?: 'Person'; id: string; firstName?: string | null; lastName?: string | null; email?: string | null; phoneDisplay?: string | null } | null;
    };
  } | null;
};

export type CancelReservationMutationVariables = Types.Exact<{
  payload: Types.TravelOfferReservationCancelPayload;
}>;

export type CancelReservationMutation = {
  __typename?: 'Mutation';
  travelOfferReservationCancel:
    | { __typename?: 'TravelOfferReservationCancelFailure'; errorMessage: string }
    | {
        __typename?: 'TravelOfferReservationCancelSuccess';
        refundedToWalletMinorUnits?: number | null;
        refundedToCardMinorUnits?: number | null;
        walletBalanceAfterMinorUnits?: number | null;
        reservation: {
          __typename?: 'TravelOfferReservation';
          id: string;
          status: Types.TravelOfferReservationStatus;
          confirmation?: {
            __typename?: 'TravelOfferReservationConfirmation';
            externalOrderId?: string | null;
            checkIn: string;
            checkOut: string;
            propertyConfirmationCode?: string | null;
            propertyInfo: {
              __typename?: 'TravelOfferReservationConfirmationPropertyInfo';
              name: string;
              address: {
                __typename?: 'TravelReservationAddress';
                line1: string;
                line2?: string | null;
                city: string;
                state?: string | null;
                country: string;
                postalCode?: string | null;
              };
            };
            baseAmount: { __typename?: 'TravelReservationMonetaryValue'; currency: Types.TravelCurrencyCode; floatingPointDecimalString: string };
            totalAmount: { __typename?: 'TravelReservationMonetaryValue'; currency: Types.TravelCurrencyCode; floatingPointDecimalString: string };
            discountAmount?: {
              __typename?: 'TravelReservationMonetaryValue';
              currency: Types.TravelCurrencyCode;
              valueInMinorUnits: number;
              floatingPointDecimalString: string;
              digits: number;
            } | null;
            billingContact: { __typename?: 'TravelOfferReservationContactInfo'; title: string; firstName: string; lastName: string; email: string; phone: string };
          } | null;
        };
      };
};

export type TravelOfferReservationByIdForCheckoutQueryVariables = Types.Exact<{
  id: Types.Scalars['String'];
}>;

export type TravelOfferReservationByIdForCheckoutQuery = {
  __typename?: 'Query';
  travelOfferReservationById?: {
    __typename?: 'TravelOfferReservation';
    id: string;
    offerToken: string;
    status: Types.TravelOfferReservationStatus;
    joyEventGroupCode?: { __typename?: 'TravelOfferReservationAppliedJoyEventGroupCode'; code: string; discountAmount: number; discountCurrency: string } | null;
    joyDiscountCoupon?: {
      __typename?: 'TravelOfferReservationAppliedJoyDiscountCoupon';
      couponCode: string;
      discountAmount: number;
      discountCurrency: string;
      discountedBaseCostMinorUnits?: number | null;
      discountedTaxesAndFeesMinorUnits?: number | null;
      isEmailRestricted: boolean;
      scopeType: Types.ScopeTypeDtoEnum;
      bookerType: Types.BookerTypeDtoEnum;
      allowedPaidWith?: Types.CouponPaidWithDtoEnum | null;
      discountBaseMinorUnits?: number | null;
    } | null;
    confirmation?: {
      __typename?: 'TravelOfferReservationConfirmation';
      billingContact: {
        __typename?: 'TravelOfferReservationContactInfo';
        email: string;
        firstName: string;
        lastName: string;
        middleName?: string | null;
        phone: string;
        title: string;
        address: {
          __typename?: 'TravelReservationAddress';
          city: string;
          country: string;
          line1: string;
          line2?: string | null;
          postalCode?: string | null;
          state?: string | null;
        };
      };
    } | null;
    roomGuests: Array<{
      __typename?: 'TravelOfferReservationRoomGuests';
      unitId: string;
      guests: Array<{
        __typename?: 'TravelOfferReservationGuestInformation';
        ageClassification: Types.TravelReservationGuestAgeClassification;
        firstName: string;
        lastName: string;
        title?: Types.TravelReservationGuestTitle | null;
      }>;
    }>;
  } | null;
};

export type SearchTravelOfferReservationsQueryVariables = Types.Exact<{
  payload: Types.SearchTravelOfferReservationsPayload;
}>;

export type SearchTravelOfferReservationsQuery = { __typename?: 'Query'; searchTravelOfferReservations?: Array<{ __typename?: 'TravelOfferReservation'; id: string }> | null };

export const TravelOfferFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOffer' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOffer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
          { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerGroupId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'providerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'availability' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'components' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'policies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'refundability' } },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferAccommodationUnitsComponent' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'distributionType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'policies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cancellationPolicies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedCancellationAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'amount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueType' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedFeeAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boardBasis' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'componentType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'occupancies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'adults' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'children' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'childrenAges' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unit' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'facilities' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'photos' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }] },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'property' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'address' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'street' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'photos' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'subject' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'userRating' },
                                    selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onsiteCharges' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'unitPrice' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'units' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fees' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'isIncludedInTotal' } },
                { kind: 'Field', name: { kind: 'Name', value: 'rate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'amount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnitsBigInt' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'baseAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'allInTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onsiteChargesTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'originalBaseAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'originalTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'prebookChange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'priceDifferencePercent' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'oldAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'newAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'cancellationChanged' } },
                { kind: 'Field', name: { kind: 'Name', value: 'boardChanged' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const TravelOfferReservationForCheckoutFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyEventGroupCode' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyDiscountCoupon' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'couponCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedBaseCostMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedTaxesAndFeesMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEmailRestricted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'scopeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'bookerType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'allowedPaidWith' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmation' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billingContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'middleName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomGuests' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'guests' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'ageClassification' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'unitId' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const AccommodationCatalogPropertyDetailsFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccommodationCatalogPropertyDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationCatalogProperty' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
          { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userRating' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'facilities' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'photos' }, selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }] } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'location' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'latitude' } },
                { kind: 'Field', name: { kind: 'Name', value: 'longitude' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                { kind: 'Field', name: { kind: 'Name', value: 'street' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const ServiceCenterAccommodationPlaceFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationPlaceV2' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'zhId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'googlePlaceId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const TravelOfferReservationFullDetailsFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationFullDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomGuests' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'guests' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'age' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ageClassification' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'unitId' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cancellationFees' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paymentMethodSummary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cardBrand' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cardLast4' } },
                { kind: 'Field', name: { kind: 'Name', value: 'walletType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'statementDescriptor' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelWallet' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'appliedMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'balanceMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refundedMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'cardPaidMinorUnits' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyDiscountCoupon' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'couponCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedBaseCostMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedTaxesAndFeesMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEmailRestricted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'scopeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'bookerType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyEventGroupCode' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmation' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otaProvider' } },
                { kind: 'Field', name: { kind: 'Name', value: 'propertyConfirmationCode' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'baseAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billingContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'middleName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
                { kind: 'Field', name: { kind: 'Name', value: 'externalOrderId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'onsiteCharges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unitPrice' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'units' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'propertyInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkOutTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInInstructions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInSpecialInstructions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'heroImageUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'policies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cancellationPolicies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedCancellationAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'amount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedFeeAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservationItems' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'occupancy' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'adults' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'children' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'refundability' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boardBasis' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unit' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'facilities' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totalAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'discountAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fees' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'isIncludedInTotal' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'amount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnitsBigInt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const TravelOfferReservationStatusDetailsFragmentDoc = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationStatusDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'eventId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isRateLock' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rateLockStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lockExpiresAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rateLockResolvedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export const EventInfoForAccommodationPropertyDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'EventInfoForAccommodationProperty' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventByName' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'name' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useEventInfoForAccommodationPropertyQuery__
 *
 * To run a query within a React component, call `useEventInfoForAccommodationPropertyQuery` and pass it any options that fit your needs.
 * When your component renders, `useEventInfoForAccommodationPropertyQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useEventInfoForAccommodationPropertyQuery({
 *   variables: {
 *      name: // value for 'name'
 *   },
 * });
 */
export function useEventInfoForAccommodationPropertyQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<EventInfoForAccommodationPropertyQuery, EventInfoForAccommodationPropertyQueryVariables>,
) {
  return ApolloReactHooks.useQuery<EventInfoForAccommodationPropertyQuery, EventInfoForAccommodationPropertyQueryVariables>(EventInfoForAccommodationPropertyDocument, baseOptions);
}
export function useEventInfoForAccommodationPropertyLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<EventInfoForAccommodationPropertyQuery, EventInfoForAccommodationPropertyQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<EventInfoForAccommodationPropertyQuery, EventInfoForAccommodationPropertyQueryVariables>(
    EventInfoForAccommodationPropertyDocument,
    baseOptions,
  );
}
export type EventInfoForAccommodationPropertyQueryHookResult = ReturnType<typeof useEventInfoForAccommodationPropertyQuery>;
export type EventInfoForAccommodationPropertyLazyQueryHookResult = ReturnType<typeof useEventInfoForAccommodationPropertyLazyQuery>;
export type EventInfoForAccommodationPropertyQueryResult = Apollo.QueryResult<EventInfoForAccommodationPropertyQuery, EventInfoForAccommodationPropertyQueryVariables>;
export const AccommodationCatalogPropertyByIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'AccommodationCatalogPropertyById' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'idObject' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationCatalogPropertyIdentifierInput' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accommodationCatalogPropertyById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'idObject' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'idObject' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccommodationCatalogPropertyDetails' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccommodationCatalogPropertyDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationCatalogProperty' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
          { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userRating' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'facilities' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'photos' }, selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }] } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'location' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'latitude' } },
                { kind: 'Field', name: { kind: 'Name', value: 'longitude' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                { kind: 'Field', name: { kind: 'Name', value: 'street' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useAccommodationCatalogPropertyByIdQuery__
 *
 * To run a query within a React component, call `useAccommodationCatalogPropertyByIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useAccommodationCatalogPropertyByIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useAccommodationCatalogPropertyByIdQuery({
 *   variables: {
 *      idObject: // value for 'idObject'
 *   },
 * });
 */
export function useAccommodationCatalogPropertyByIdQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<AccommodationCatalogPropertyByIdQuery, AccommodationCatalogPropertyByIdQueryVariables>,
) {
  return ApolloReactHooks.useQuery<AccommodationCatalogPropertyByIdQuery, AccommodationCatalogPropertyByIdQueryVariables>(AccommodationCatalogPropertyByIdDocument, baseOptions);
}
export function useAccommodationCatalogPropertyByIdLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<AccommodationCatalogPropertyByIdQuery, AccommodationCatalogPropertyByIdQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<AccommodationCatalogPropertyByIdQuery, AccommodationCatalogPropertyByIdQueryVariables>(
    AccommodationCatalogPropertyByIdDocument,
    baseOptions,
  );
}
export type AccommodationCatalogPropertyByIdQueryHookResult = ReturnType<typeof useAccommodationCatalogPropertyByIdQuery>;
export type AccommodationCatalogPropertyByIdLazyQueryHookResult = ReturnType<typeof useAccommodationCatalogPropertyByIdLazyQuery>;
export type AccommodationCatalogPropertyByIdQueryResult = Apollo.QueryResult<AccommodationCatalogPropertyByIdQuery, AccommodationCatalogPropertyByIdQueryVariables>;
export const PropertiesWithAccommodationPlaceDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'PropertiesWithAccommodationPlace' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'idObject' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationCatalogPropertyIdentifierInput' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'accommodationPlaceId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'pageContainerBySlugPayload2' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'PageContainerBySlugPayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accommodationCatalogPropertyById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'idObject' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'idObject' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'AccommodationCatalogPropertyDetails' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'accommodationPlaceV2ById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'accommodationPlaceId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isPhotoHidden' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'photo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'altTag' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'customPhoto' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'altTag' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                { kind: 'Field', name: { kind: 'Name', value: 'customName' } },
                { kind: 'Field', name: { kind: 'Name', value: 'userNote' } },
                { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                { kind: 'Field', name: { kind: 'Name', value: 'lat' } },
                { kind: 'Field', name: { kind: 'Name', value: 'long' } },
                { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                { kind: 'Field', name: { kind: 'Name', value: 'address' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pageContainerBySlug' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'pageContainerBySlugPayload2' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'components' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'InlineFragment',
                        typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TextComponent' } },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'AccommodationCatalogPropertyDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationCatalogProperty' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'name' } },
          { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
          { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'userRating' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'facilities' },
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'photos' }, selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }] } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'location' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'latitude' } },
                { kind: 'Field', name: { kind: 'Name', value: 'longitude' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'address' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                { kind: 'Field', name: { kind: 'Name', value: 'street' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __usePropertiesWithAccommodationPlaceQuery__
 *
 * To run a query within a React component, call `usePropertiesWithAccommodationPlaceQuery` and pass it any options that fit your needs.
 * When your component renders, `usePropertiesWithAccommodationPlaceQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePropertiesWithAccommodationPlaceQuery({
 *   variables: {
 *      idObject: // value for 'idObject'
 *      accommodationPlaceId: // value for 'accommodationPlaceId'
 *      pageContainerBySlugPayload2: // value for 'pageContainerBySlugPayload2'
 *   },
 * });
 */
export function usePropertiesWithAccommodationPlaceQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<PropertiesWithAccommodationPlaceQuery, PropertiesWithAccommodationPlaceQueryVariables>,
) {
  return ApolloReactHooks.useQuery<PropertiesWithAccommodationPlaceQuery, PropertiesWithAccommodationPlaceQueryVariables>(PropertiesWithAccommodationPlaceDocument, baseOptions);
}
export function usePropertiesWithAccommodationPlaceLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<PropertiesWithAccommodationPlaceQuery, PropertiesWithAccommodationPlaceQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<PropertiesWithAccommodationPlaceQuery, PropertiesWithAccommodationPlaceQueryVariables>(
    PropertiesWithAccommodationPlaceDocument,
    baseOptions,
  );
}
export type PropertiesWithAccommodationPlaceQueryHookResult = ReturnType<typeof usePropertiesWithAccommodationPlaceQuery>;
export type PropertiesWithAccommodationPlaceLazyQueryHookResult = ReturnType<typeof usePropertiesWithAccommodationPlaceLazyQuery>;
export type PropertiesWithAccommodationPlaceQueryResult = Apollo.QueryResult<PropertiesWithAccommodationPlaceQuery, PropertiesWithAccommodationPlaceQueryVariables>;
export const TravelOffersForAccommodationPropertiesDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TravelOffersForAccommodationProperties' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOffersForAccommodationForPropertiesPayload' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'includeWalletSpend' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } } },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'includeRateLockEligibility' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } } },
          defaultValue: { kind: 'BooleanValue', value: false },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOffersForAccommodationProperties' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'someOffers' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: '_diag' } },
                      { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOffer' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'rateLockEligibility' },
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [
                              { kind: 'Argument', name: { kind: 'Name', value: 'if' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'includeRateLockEligibility' } } },
                            ],
                          },
                        ],
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'eligible' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'walletSpend' },
                        arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'eventId' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
                        directives: [
                          {
                            kind: 'Directive',
                            name: { kind: 'Name', value: 'include' },
                            arguments: [
                              { kind: 'Argument', name: { kind: 'Name', value: 'if' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'includeWalletSpend' } } },
                            ],
                          },
                        ],
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'coverage' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'partialAllowed' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'fundAppliedMinorUnits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'cardDueNowMinorUnits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'fundBalanceMinorUnits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'suggestedCoupon' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'discountType' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'discountValue' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'discountAmountMinorUnits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'couponPaidWith' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOffer' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOffer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
          { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerGroupId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'providerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'availability' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'components' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'policies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'refundability' } },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferAccommodationUnitsComponent' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'distributionType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'policies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cancellationPolicies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedCancellationAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'amount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueType' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedFeeAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boardBasis' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'componentType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'occupancies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'adults' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'children' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'childrenAges' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unit' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'facilities' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'photos' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }] },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'property' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'address' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'street' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'photos' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'subject' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'userRating' },
                                    selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onsiteCharges' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'unitPrice' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'units' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fees' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'isIncludedInTotal' } },
                { kind: 'Field', name: { kind: 'Name', value: 'rate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'amount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnitsBigInt' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'baseAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'allInTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onsiteChargesTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'originalBaseAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'originalTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'prebookChange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'priceDifferencePercent' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'oldAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'newAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'cancellationChanged' } },
                { kind: 'Field', name: { kind: 'Name', value: 'boardChanged' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useTravelOffersForAccommodationPropertiesQuery__
 *
 * To run a query within a React component, call `useTravelOffersForAccommodationPropertiesQuery` and pass it any options that fit your needs.
 * When your component renders, `useTravelOffersForAccommodationPropertiesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTravelOffersForAccommodationPropertiesQuery({
 *   variables: {
 *      payload: // value for 'payload'
 *      eventId: // value for 'eventId'
 *      includeWalletSpend: // value for 'includeWalletSpend'
 *      includeRateLockEligibility: // value for 'includeRateLockEligibility'
 *   },
 * });
 */
export function useTravelOffersForAccommodationPropertiesQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<TravelOffersForAccommodationPropertiesQuery, TravelOffersForAccommodationPropertiesQueryVariables>,
) {
  return ApolloReactHooks.useQuery<TravelOffersForAccommodationPropertiesQuery, TravelOffersForAccommodationPropertiesQueryVariables>(
    TravelOffersForAccommodationPropertiesDocument,
    baseOptions,
  );
}
export function useTravelOffersForAccommodationPropertiesLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<TravelOffersForAccommodationPropertiesQuery, TravelOffersForAccommodationPropertiesQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<TravelOffersForAccommodationPropertiesQuery, TravelOffersForAccommodationPropertiesQueryVariables>(
    TravelOffersForAccommodationPropertiesDocument,
    baseOptions,
  );
}
export type TravelOffersForAccommodationPropertiesQueryHookResult = ReturnType<typeof useTravelOffersForAccommodationPropertiesQuery>;
export type TravelOffersForAccommodationPropertiesLazyQueryHookResult = ReturnType<typeof useTravelOffersForAccommodationPropertiesLazyQuery>;
export type TravelOffersForAccommodationPropertiesQueryResult = Apollo.QueryResult<
  TravelOffersForAccommodationPropertiesQuery,
  TravelOffersForAccommodationPropertiesQueryVariables
>;
export const PricingsForAccommodationByRegionDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'PricingsForAccommodationByRegion' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelPricingsForAccommodationByRegionPayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'pricingsForAccommodationByRegion' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'pricings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'property' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'relevanceScore' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'userRating' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'location' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'latitude' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'longitude' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'timezone' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'photos' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'subject' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'totalAmount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __usePricingsForAccommodationByRegionQuery__
 *
 * To run a query within a React component, call `usePricingsForAccommodationByRegionQuery` and pass it any options that fit your needs.
 * When your component renders, `usePricingsForAccommodationByRegionQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePricingsForAccommodationByRegionQuery({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function usePricingsForAccommodationByRegionQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<PricingsForAccommodationByRegionQuery, PricingsForAccommodationByRegionQueryVariables>,
) {
  return ApolloReactHooks.useQuery<PricingsForAccommodationByRegionQuery, PricingsForAccommodationByRegionQueryVariables>(PricingsForAccommodationByRegionDocument, baseOptions);
}
export function usePricingsForAccommodationByRegionLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<PricingsForAccommodationByRegionQuery, PricingsForAccommodationByRegionQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<PricingsForAccommodationByRegionQuery, PricingsForAccommodationByRegionQueryVariables>(
    PricingsForAccommodationByRegionDocument,
    baseOptions,
  );
}
export type PricingsForAccommodationByRegionQueryHookResult = ReturnType<typeof usePricingsForAccommodationByRegionQuery>;
export type PricingsForAccommodationByRegionLazyQueryHookResult = ReturnType<typeof usePricingsForAccommodationByRegionLazyQuery>;
export type PricingsForAccommodationByRegionQueryResult = Apollo.QueryResult<PricingsForAccommodationByRegionQuery, PricingsForAccommodationByRegionQueryVariables>;
export const ServiceCenterAddAccommodationPlaceV2Document = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ServiceCenterAddAccommodationPlaceV2' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'AddAccommodationPlaceV2Request' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'addAccommodationPlaceV2' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationPlaceV2' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'zhId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'googlePlaceId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type ServiceCenterAddAccommodationPlaceV2MutationFn = Apollo.MutationFunction<
  ServiceCenterAddAccommodationPlaceV2Mutation,
  ServiceCenterAddAccommodationPlaceV2MutationVariables
>;

/**
 * __useServiceCenterAddAccommodationPlaceV2Mutation__
 *
 * To run a mutation, you first call `useServiceCenterAddAccommodationPlaceV2Mutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useServiceCenterAddAccommodationPlaceV2Mutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [serviceCenterAddAccommodationPlaceV2Mutation, { data, loading, error }] = useServiceCenterAddAccommodationPlaceV2Mutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useServiceCenterAddAccommodationPlaceV2Mutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<ServiceCenterAddAccommodationPlaceV2Mutation, ServiceCenterAddAccommodationPlaceV2MutationVariables>,
) {
  return ApolloReactHooks.useMutation<ServiceCenterAddAccommodationPlaceV2Mutation, ServiceCenterAddAccommodationPlaceV2MutationVariables>(
    ServiceCenterAddAccommodationPlaceV2Document,
    baseOptions,
  );
}
export type ServiceCenterAddAccommodationPlaceV2MutationHookResult = ReturnType<typeof useServiceCenterAddAccommodationPlaceV2Mutation>;
export type ServiceCenterAddAccommodationPlaceV2MutationResult = Apollo.MutationResult<ServiceCenterAddAccommodationPlaceV2Mutation>;
export type ServiceCenterAddAccommodationPlaceV2MutationOptions = Apollo.BaseMutationOptions<
  ServiceCenterAddAccommodationPlaceV2Mutation,
  ServiceCenterAddAccommodationPlaceV2MutationVariables
>;
export const ServiceCenterDeleteAccommodationPlaceV2Document = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'ServiceCenterDeleteAccommodationPlaceV2' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'deleteAccommodationPlaceV2' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationPlaceV2' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'zhId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'googlePlaceId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type ServiceCenterDeleteAccommodationPlaceV2MutationFn = Apollo.MutationFunction<
  ServiceCenterDeleteAccommodationPlaceV2Mutation,
  ServiceCenterDeleteAccommodationPlaceV2MutationVariables
>;

/**
 * __useServiceCenterDeleteAccommodationPlaceV2Mutation__
 *
 * To run a mutation, you first call `useServiceCenterDeleteAccommodationPlaceV2Mutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useServiceCenterDeleteAccommodationPlaceV2Mutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [serviceCenterDeleteAccommodationPlaceV2Mutation, { data, loading, error }] = useServiceCenterDeleteAccommodationPlaceV2Mutation({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useServiceCenterDeleteAccommodationPlaceV2Mutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<ServiceCenterDeleteAccommodationPlaceV2Mutation, ServiceCenterDeleteAccommodationPlaceV2MutationVariables>,
) {
  return ApolloReactHooks.useMutation<ServiceCenterDeleteAccommodationPlaceV2Mutation, ServiceCenterDeleteAccommodationPlaceV2MutationVariables>(
    ServiceCenterDeleteAccommodationPlaceV2Document,
    baseOptions,
  );
}
export type ServiceCenterDeleteAccommodationPlaceV2MutationHookResult = ReturnType<typeof useServiceCenterDeleteAccommodationPlaceV2Mutation>;
export type ServiceCenterDeleteAccommodationPlaceV2MutationResult = Apollo.MutationResult<ServiceCenterDeleteAccommodationPlaceV2Mutation>;
export type ServiceCenterDeleteAccommodationPlaceV2MutationOptions = Apollo.BaseMutationOptions<
  ServiceCenterDeleteAccommodationPlaceV2Mutation,
  ServiceCenterDeleteAccommodationPlaceV2MutationVariables
>;
export const TravelOfferReservationUpdateDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TravelOfferReservationUpdate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationUpdatePayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationUpdate' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'status' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type TravelOfferReservationUpdateMutationFn = Apollo.MutationFunction<TravelOfferReservationUpdateMutation, TravelOfferReservationUpdateMutationVariables>;

/**
 * __useTravelOfferReservationUpdateMutation__
 *
 * To run a mutation, you first call `useTravelOfferReservationUpdateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationUpdateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [travelOfferReservationUpdateMutation, { data, loading, error }] = useTravelOfferReservationUpdateMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useTravelOfferReservationUpdateMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<TravelOfferReservationUpdateMutation, TravelOfferReservationUpdateMutationVariables>,
) {
  return ApolloReactHooks.useMutation<TravelOfferReservationUpdateMutation, TravelOfferReservationUpdateMutationVariables>(TravelOfferReservationUpdateDocument, baseOptions);
}
export type TravelOfferReservationUpdateMutationHookResult = ReturnType<typeof useTravelOfferReservationUpdateMutation>;
export type TravelOfferReservationUpdateMutationResult = Apollo.MutationResult<TravelOfferReservationUpdateMutation>;
export type TravelOfferReservationUpdateMutationOptions = Apollo.BaseMutationOptions<TravelOfferReservationUpdateMutation, TravelOfferReservationUpdateMutationVariables>;
export const ServiceCenterAccommodationPlacesDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'ServiceCenterAccommodationPlaces' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'name' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventByName' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'name' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'name' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'accommodationPlacesV2' },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' } }] },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'ServiceCenterAccommodationPlace' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'AccommodationPlaceV2' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'type' } },
          { kind: 'Field', name: { kind: 'Name', value: 'zhId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'googlePlaceId' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useServiceCenterAccommodationPlacesQuery__
 *
 * To run a query within a React component, call `useServiceCenterAccommodationPlacesQuery` and pass it any options that fit your needs.
 * When your component renders, `useServiceCenterAccommodationPlacesQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useServiceCenterAccommodationPlacesQuery({
 *   variables: {
 *      name: // value for 'name'
 *   },
 * });
 */
export function useServiceCenterAccommodationPlacesQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<ServiceCenterAccommodationPlacesQuery, ServiceCenterAccommodationPlacesQueryVariables>,
) {
  return ApolloReactHooks.useQuery<ServiceCenterAccommodationPlacesQuery, ServiceCenterAccommodationPlacesQueryVariables>(ServiceCenterAccommodationPlacesDocument, baseOptions);
}
export function useServiceCenterAccommodationPlacesLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<ServiceCenterAccommodationPlacesQuery, ServiceCenterAccommodationPlacesQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<ServiceCenterAccommodationPlacesQuery, ServiceCenterAccommodationPlacesQueryVariables>(
    ServiceCenterAccommodationPlacesDocument,
    baseOptions,
  );
}
export type ServiceCenterAccommodationPlacesQueryHookResult = ReturnType<typeof useServiceCenterAccommodationPlacesQuery>;
export type ServiceCenterAccommodationPlacesLazyQueryHookResult = ReturnType<typeof useServiceCenterAccommodationPlacesLazyQuery>;
export type ServiceCenterAccommodationPlacesQueryResult = Apollo.QueryResult<ServiceCenterAccommodationPlacesQuery, ServiceCenterAccommodationPlacesQueryVariables>;
export const TravelOfferFromOfferTokenDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TravelOfferFromOfferToken' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'params' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferFromOfferTokenParams' } },
        },
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'useCache' } },
          type: { kind: 'NamedType', name: { kind: 'Name', value: 'Boolean' } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferFromOfferToken' },
            arguments: [
              { kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } },
              { kind: 'Argument', name: { kind: 'Name', value: 'params' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'params' } } },
              { kind: 'Argument', name: { kind: 'Name', value: 'useCache' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'useCache' } } },
            ],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOffer' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'rateLockEligibility' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'eligible' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'freeCancellationUntil' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOffer' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOffer' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
          { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerGroupId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'providerName' } },
          { kind: 'Field', name: { kind: 'Name', value: 'availability' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'components' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'policies' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'refundability' } },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferAccommodationUnitsComponent' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'distributionType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'policies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cancellationPolicies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedCancellationAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'amount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueType' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedFeeAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boardBasis' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'componentType' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'occupancies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'adults' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'children' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'childrenAges' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unit' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'plainTextLongDescription' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'facilities' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: '__typename' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'photos' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'url' } }] },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'property' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'starRating' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'address' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'street' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'photos' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'url' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'subject' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              { kind: 'Field', name: { kind: 'Name', value: 'label' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'userRating' },
                                    selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'UserRating' } }] },
                                  },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onsiteCharges' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'unitPrice' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'units' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'fees' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'isIncludedInTotal' } },
                { kind: 'Field', name: { kind: 'Name', value: 'rate' } },
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'amount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnitsBigInt' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'baseAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'totalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'allInTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'onsiteChargesTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'originalBaseAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'originalTotalAmount' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'prebookChange' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'priceDifferencePercent' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'oldAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'newAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'cancellationChanged' } },
                { kind: 'Field', name: { kind: 'Name', value: 'boardChanged' } },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'UserRating' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'UserRating' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'ratingCount' } },
          { kind: 'Field', name: { kind: 'Name', value: 'ratingValue' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useTravelOfferFromOfferTokenQuery__
 *
 * To run a query within a React component, call `useTravelOfferFromOfferTokenQuery` and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferFromOfferTokenQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTravelOfferFromOfferTokenQuery({
 *   variables: {
 *      payload: // value for 'payload'
 *      params: // value for 'params'
 *      useCache: // value for 'useCache'
 *   },
 * });
 */
export function useTravelOfferFromOfferTokenQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<TravelOfferFromOfferTokenQuery, TravelOfferFromOfferTokenQueryVariables>) {
  return ApolloReactHooks.useQuery<TravelOfferFromOfferTokenQuery, TravelOfferFromOfferTokenQueryVariables>(TravelOfferFromOfferTokenDocument, baseOptions);
}
export function useTravelOfferFromOfferTokenLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<TravelOfferFromOfferTokenQuery, TravelOfferFromOfferTokenQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<TravelOfferFromOfferTokenQuery, TravelOfferFromOfferTokenQueryVariables>(TravelOfferFromOfferTokenDocument, baseOptions);
}
export type TravelOfferFromOfferTokenQueryHookResult = ReturnType<typeof useTravelOfferFromOfferTokenQuery>;
export type TravelOfferFromOfferTokenLazyQueryHookResult = ReturnType<typeof useTravelOfferFromOfferTokenLazyQuery>;
export type TravelOfferFromOfferTokenQueryResult = Apollo.QueryResult<TravelOfferFromOfferTokenQuery, TravelOfferFromOfferTokenQueryVariables>;
export const TravelOfferReservationCreateDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TravelOfferReservationCreate' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCreatePayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationCreate' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCreateVaulteraVaultResult' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'reservation' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'vaulteraToken' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCreateStripeNativeResult' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'reservation' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'paymentIntentClientSecret' } },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCreateTravelWalletResult' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        alias: { kind: 'Name', value: 'walletReservation' },
                        name: { kind: 'Name', value: 'reservation' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' } }] },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'outcome' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'failureCode' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'failureReason' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyEventGroupCode' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyDiscountCoupon' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'couponCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedBaseCostMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedTaxesAndFeesMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEmailRestricted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'scopeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'bookerType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'allowedPaidWith' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmation' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billingContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'middleName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomGuests' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'guests' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'ageClassification' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'unitId' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type TravelOfferReservationCreateMutationFn = Apollo.MutationFunction<TravelOfferReservationCreateMutation, TravelOfferReservationCreateMutationVariables>;

/**
 * __useTravelOfferReservationCreateMutation__
 *
 * To run a mutation, you first call `useTravelOfferReservationCreateMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationCreateMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [travelOfferReservationCreateMutation, { data, loading, error }] = useTravelOfferReservationCreateMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useTravelOfferReservationCreateMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<TravelOfferReservationCreateMutation, TravelOfferReservationCreateMutationVariables>,
) {
  return ApolloReactHooks.useMutation<TravelOfferReservationCreateMutation, TravelOfferReservationCreateMutationVariables>(TravelOfferReservationCreateDocument, baseOptions);
}
export type TravelOfferReservationCreateMutationHookResult = ReturnType<typeof useTravelOfferReservationCreateMutation>;
export type TravelOfferReservationCreateMutationResult = Apollo.MutationResult<TravelOfferReservationCreateMutation>;
export type TravelOfferReservationCreateMutationOptions = Apollo.BaseMutationOptions<TravelOfferReservationCreateMutation, TravelOfferReservationCreateMutationVariables>;
export const TravelOfferReservationConfirmDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'TravelOfferReservationConfirm' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationConfirmPayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationConfirm' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationConfirmSuccess' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'reservation' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationConfirmFailure' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'errorMessage' } }] },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type TravelOfferReservationConfirmMutationFn = Apollo.MutationFunction<TravelOfferReservationConfirmMutation, TravelOfferReservationConfirmMutationVariables>;

/**
 * __useTravelOfferReservationConfirmMutation__
 *
 * To run a mutation, you first call `useTravelOfferReservationConfirmMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationConfirmMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [travelOfferReservationConfirmMutation, { data, loading, error }] = useTravelOfferReservationConfirmMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useTravelOfferReservationConfirmMutation(
  baseOptions?: ApolloReactHooks.MutationHookOptions<TravelOfferReservationConfirmMutation, TravelOfferReservationConfirmMutationVariables>,
) {
  return ApolloReactHooks.useMutation<TravelOfferReservationConfirmMutation, TravelOfferReservationConfirmMutationVariables>(TravelOfferReservationConfirmDocument, baseOptions);
}
export type TravelOfferReservationConfirmMutationHookResult = ReturnType<typeof useTravelOfferReservationConfirmMutation>;
export type TravelOfferReservationConfirmMutationResult = Apollo.MutationResult<TravelOfferReservationConfirmMutation>;
export type TravelOfferReservationConfirmMutationOptions = Apollo.BaseMutationOptions<TravelOfferReservationConfirmMutation, TravelOfferReservationConfirmMutationVariables>;
export const TravelOfferReservationByIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TravelOfferReservationById' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'travelOfferReservationByIdId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'travelOfferReservationByIdId' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationFullDetails' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationFullDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomGuests' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'guests' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'age' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ageClassification' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'unitId' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cancellationFees' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paymentMethodSummary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cardBrand' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cardLast4' } },
                { kind: 'Field', name: { kind: 'Name', value: 'walletType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'statementDescriptor' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelWallet' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'appliedMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'balanceMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refundedMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'cardPaidMinorUnits' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyDiscountCoupon' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'couponCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedBaseCostMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedTaxesAndFeesMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEmailRestricted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'scopeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'bookerType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyEventGroupCode' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmation' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otaProvider' } },
                { kind: 'Field', name: { kind: 'Name', value: 'propertyConfirmationCode' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'baseAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billingContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'middleName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
                { kind: 'Field', name: { kind: 'Name', value: 'externalOrderId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'onsiteCharges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unitPrice' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'units' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'propertyInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkOutTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInInstructions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInSpecialInstructions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'heroImageUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'policies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cancellationPolicies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedCancellationAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'amount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedFeeAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservationItems' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'occupancy' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'adults' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'children' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'refundability' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boardBasis' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unit' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'facilities' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totalAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'discountAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fees' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'isIncludedInTotal' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'amount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnitsBigInt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useTravelOfferReservationByIdQuery__
 *
 * To run a query within a React component, call `useTravelOfferReservationByIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationByIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTravelOfferReservationByIdQuery({
 *   variables: {
 *      travelOfferReservationByIdId: // value for 'travelOfferReservationByIdId'
 *   },
 * });
 */
export function useTravelOfferReservationByIdQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<TravelOfferReservationByIdQuery, TravelOfferReservationByIdQueryVariables>) {
  return ApolloReactHooks.useQuery<TravelOfferReservationByIdQuery, TravelOfferReservationByIdQueryVariables>(TravelOfferReservationByIdDocument, baseOptions);
}
export function useTravelOfferReservationByIdLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<TravelOfferReservationByIdQuery, TravelOfferReservationByIdQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<TravelOfferReservationByIdQuery, TravelOfferReservationByIdQueryVariables>(TravelOfferReservationByIdDocument, baseOptions);
}
export type TravelOfferReservationByIdQueryHookResult = ReturnType<typeof useTravelOfferReservationByIdQuery>;
export type TravelOfferReservationByIdLazyQueryHookResult = ReturnType<typeof useTravelOfferReservationByIdLazyQuery>;
export type TravelOfferReservationByIdQueryResult = Apollo.QueryResult<TravelOfferReservationByIdQuery, TravelOfferReservationByIdQueryVariables>;
export const TravelOfferReservationByIdOrOrderIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TravelOfferReservationByIdOrOrderId' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'travelOfferReservationByIdOrOrderIdId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationByIdOrOrderId' },
            arguments: [
              { kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'travelOfferReservationByIdOrOrderIdId' } } },
            ],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationFullDetails' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationFullDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomGuests' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'guests' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'age' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'ageClassification' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phoneNumber' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'unitId' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'email' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'cancellationFees' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'paymentMethodSummary' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cardBrand' } },
                { kind: 'Field', name: { kind: 'Name', value: 'cardLast4' } },
                { kind: 'Field', name: { kind: 'Name', value: 'walletType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'statementDescriptor' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelWallet' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'appliedMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'balanceMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'refundedMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
              ],
            },
          },
          { kind: 'Field', name: { kind: 'Name', value: 'cardPaidMinorUnits' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyDiscountCoupon' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'couponCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedBaseCostMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedTaxesAndFeesMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEmailRestricted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'scopeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'bookerType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyEventGroupCode' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmation' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'provider' } },
                { kind: 'Field', name: { kind: 'Name', value: 'otaProvider' } },
                { kind: 'Field', name: { kind: 'Name', value: 'propertyConfirmationCode' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'baseAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billingContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'middleName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
                { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
                { kind: 'Field', name: { kind: 'Name', value: 'externalOrderId' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'onsiteCharges' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unitPrice' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'units' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'propertyInfo' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkOutTime' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInInstructions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInSpecialInstructions' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'heroImageUrl' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'policies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                          ],
                        },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'cancellationPolicies' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'start' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'end' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedCancellationAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'amount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                      ],
                                    },
                                  },
                                ],
                              },
                            },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'estimatedFeeAmount' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'reservationItems' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'occupancy' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'adults' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'children' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'refundability' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'boardBasis' },
                        selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                      },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'unit' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'description' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'facilities' },
                              selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'label' } }] },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'totalAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'discountAmount' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'fees' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'isIncludedInTotal' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'rate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'type' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'visibility' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'amount' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnitsBigInt' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useTravelOfferReservationByIdOrOrderIdQuery__
 *
 * To run a query within a React component, call `useTravelOfferReservationByIdOrOrderIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationByIdOrOrderIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTravelOfferReservationByIdOrOrderIdQuery({
 *   variables: {
 *      travelOfferReservationByIdOrOrderIdId: // value for 'travelOfferReservationByIdOrOrderIdId'
 *   },
 * });
 */
export function useTravelOfferReservationByIdOrOrderIdQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<TravelOfferReservationByIdOrOrderIdQuery, TravelOfferReservationByIdOrOrderIdQueryVariables>,
) {
  return ApolloReactHooks.useQuery<TravelOfferReservationByIdOrOrderIdQuery, TravelOfferReservationByIdOrOrderIdQueryVariables>(
    TravelOfferReservationByIdOrOrderIdDocument,
    baseOptions,
  );
}
export function useTravelOfferReservationByIdOrOrderIdLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<TravelOfferReservationByIdOrOrderIdQuery, TravelOfferReservationByIdOrOrderIdQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<TravelOfferReservationByIdOrOrderIdQuery, TravelOfferReservationByIdOrOrderIdQueryVariables>(
    TravelOfferReservationByIdOrOrderIdDocument,
    baseOptions,
  );
}
export type TravelOfferReservationByIdOrOrderIdQueryHookResult = ReturnType<typeof useTravelOfferReservationByIdOrOrderIdQuery>;
export type TravelOfferReservationByIdOrOrderIdLazyQueryHookResult = ReturnType<typeof useTravelOfferReservationByIdOrOrderIdLazyQuery>;
export type TravelOfferReservationByIdOrOrderIdQueryResult = Apollo.QueryResult<TravelOfferReservationByIdOrOrderIdQuery, TravelOfferReservationByIdOrOrderIdQueryVariables>;
export const TravelOfferReservationStatusByIdOrOrderIdDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TravelOfferReservationStatusByIdOrOrderId' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'travelOfferReservationStatusByIdOrOrderId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationByIdOrOrderId' },
            arguments: [
              { kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'travelOfferReservationStatusByIdOrOrderId' } } },
            ],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationStatusDetails' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationStatusDetails' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          { kind: 'Field', name: { kind: 'Name', value: 'joyPlaceId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'eventId' } },
          { kind: 'Field', name: { kind: 'Name', value: 'isRateLock' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rateLockStatus' } },
          { kind: 'Field', name: { kind: 'Name', value: 'lockExpiresAt' } },
          { kind: 'Field', name: { kind: 'Name', value: 'rateLockResolvedAt' } },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useTravelOfferReservationStatusByIdOrOrderIdQuery__
 *
 * To run a query within a React component, call `useTravelOfferReservationStatusByIdOrOrderIdQuery` and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationStatusByIdOrOrderIdQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTravelOfferReservationStatusByIdOrOrderIdQuery({
 *   variables: {
 *      travelOfferReservationStatusByIdOrOrderId: // value for 'travelOfferReservationStatusByIdOrOrderId'
 *   },
 * });
 */
export function useTravelOfferReservationStatusByIdOrOrderIdQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<TravelOfferReservationStatusByIdOrOrderIdQuery, TravelOfferReservationStatusByIdOrOrderIdQueryVariables>,
) {
  return ApolloReactHooks.useQuery<TravelOfferReservationStatusByIdOrOrderIdQuery, TravelOfferReservationStatusByIdOrOrderIdQueryVariables>(
    TravelOfferReservationStatusByIdOrOrderIdDocument,
    baseOptions,
  );
}
export function useTravelOfferReservationStatusByIdOrOrderIdLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<TravelOfferReservationStatusByIdOrOrderIdQuery, TravelOfferReservationStatusByIdOrOrderIdQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<TravelOfferReservationStatusByIdOrOrderIdQuery, TravelOfferReservationStatusByIdOrOrderIdQueryVariables>(
    TravelOfferReservationStatusByIdOrOrderIdDocument,
    baseOptions,
  );
}
export type TravelOfferReservationStatusByIdOrOrderIdQueryHookResult = ReturnType<typeof useTravelOfferReservationStatusByIdOrOrderIdQuery>;
export type TravelOfferReservationStatusByIdOrOrderIdLazyQueryHookResult = ReturnType<typeof useTravelOfferReservationStatusByIdOrOrderIdLazyQuery>;
export type TravelOfferReservationStatusByIdOrOrderIdQueryResult = Apollo.QueryResult<
  TravelOfferReservationStatusByIdOrOrderIdQuery,
  TravelOfferReservationStatusByIdOrOrderIdQueryVariables
>;
export const GetEventByIdForReservationStatusPageDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'GetEventByIdForReservationStatusPage' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                { kind: 'Field', name: { kind: 'Name', value: 'website' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'travelSettings' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'checkInDate' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'checkOutDate' } },
                    ],
                  },
                },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'info' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'eventDisplayName' } },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useGetEventByIdForReservationStatusPageQuery__
 *
 * To run a query within a React component, call `useGetEventByIdForReservationStatusPageQuery` and pass it any options that fit your needs.
 * When your component renders, `useGetEventByIdForReservationStatusPageQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useGetEventByIdForReservationStatusPageQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useGetEventByIdForReservationStatusPageQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<GetEventByIdForReservationStatusPageQuery, GetEventByIdForReservationStatusPageQueryVariables>,
) {
  return ApolloReactHooks.useQuery<GetEventByIdForReservationStatusPageQuery, GetEventByIdForReservationStatusPageQueryVariables>(
    GetEventByIdForReservationStatusPageDocument,
    baseOptions,
  );
}
export function useGetEventByIdForReservationStatusPageLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<GetEventByIdForReservationStatusPageQuery, GetEventByIdForReservationStatusPageQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<GetEventByIdForReservationStatusPageQuery, GetEventByIdForReservationStatusPageQueryVariables>(
    GetEventByIdForReservationStatusPageDocument,
    baseOptions,
  );
}
export type GetEventByIdForReservationStatusPageQueryHookResult = ReturnType<typeof useGetEventByIdForReservationStatusPageQuery>;
export type GetEventByIdForReservationStatusPageLazyQueryHookResult = ReturnType<typeof useGetEventByIdForReservationStatusPageLazyQuery>;
export type GetEventByIdForReservationStatusPageQueryResult = Apollo.QueryResult<GetEventByIdForReservationStatusPageQuery, GetEventByIdForReservationStatusPageQueryVariables>;
export const CurrentEventGuestDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'CurrentEventGuest' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'ID' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'eventById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'eventId' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'activeSession' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'person' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'phoneDisplay' } },
                          ],
                        },
                      },
                    ],
                  },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useCurrentEventGuestQuery__
 *
 * To run a query within a React component, call `useCurrentEventGuestQuery` and pass it any options that fit your needs.
 * When your component renders, `useCurrentEventGuestQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useCurrentEventGuestQuery({
 *   variables: {
 *      eventId: // value for 'eventId'
 *   },
 * });
 */
export function useCurrentEventGuestQuery(baseOptions?: ApolloReactHooks.QueryHookOptions<CurrentEventGuestQuery, CurrentEventGuestQueryVariables>) {
  return ApolloReactHooks.useQuery<CurrentEventGuestQuery, CurrentEventGuestQueryVariables>(CurrentEventGuestDocument, baseOptions);
}
export function useCurrentEventGuestLazyQuery(baseOptions?: ApolloReactHooks.LazyQueryHookOptions<CurrentEventGuestQuery, CurrentEventGuestQueryVariables>) {
  return ApolloReactHooks.useLazyQuery<CurrentEventGuestQuery, CurrentEventGuestQueryVariables>(CurrentEventGuestDocument, baseOptions);
}
export type CurrentEventGuestQueryHookResult = ReturnType<typeof useCurrentEventGuestQuery>;
export type CurrentEventGuestLazyQueryHookResult = ReturnType<typeof useCurrentEventGuestLazyQuery>;
export type CurrentEventGuestQueryResult = Apollo.QueryResult<CurrentEventGuestQuery, CurrentEventGuestQueryVariables>;
export const CancelReservationDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'mutation',
      name: { kind: 'Name', value: 'CancelReservation' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCancelPayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationCancel' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCancelSuccess' } },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'refundedToWalletMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'refundedToCardMinorUnits' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'walletBalanceAfterMinorUnits' } },
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'reservation' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'id' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'status' } },
                            {
                              kind: 'Field',
                              name: { kind: 'Name', value: 'confirmation' },
                              selectionSet: {
                                kind: 'SelectionSet',
                                selections: [
                                  { kind: 'Field', name: { kind: 'Name', value: 'externalOrderId' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'propertyInfo' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'name' } },
                                        {
                                          kind: 'Field',
                                          name: { kind: 'Name', value: 'address' },
                                          selectionSet: {
                                            kind: 'SelectionSet',
                                            selections: [
                                              { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                                              { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                                            ],
                                          },
                                        },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'checkIn' } },
                                  { kind: 'Field', name: { kind: 'Name', value: 'checkOut' } },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'baseAmount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'totalAmount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'discountAmount' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'currency' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'valueInMinorUnits' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'floatingPointDecimalString' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'digits' } },
                                      ],
                                    },
                                  },
                                  {
                                    kind: 'Field',
                                    name: { kind: 'Name', value: 'billingContact' },
                                    selectionSet: {
                                      kind: 'SelectionSet',
                                      selections: [
                                        { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                                        { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                                      ],
                                    },
                                  },
                                  { kind: 'Field', name: { kind: 'Name', value: 'propertyConfirmationCode' } },
                                ],
                              },
                            },
                          ],
                        },
                      },
                    ],
                  },
                },
                {
                  kind: 'InlineFragment',
                  typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservationCancelFailure' } },
                  selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'errorMessage' } }] },
                },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;
export type CancelReservationMutationFn = Apollo.MutationFunction<CancelReservationMutation, CancelReservationMutationVariables>;

/**
 * __useCancelReservationMutation__
 *
 * To run a mutation, you first call `useCancelReservationMutation` within a React component and pass it any options that fit your needs.
 * When your component renders, `useCancelReservationMutation` returns a tuple that includes:
 * - A mutate function that you can call at any time to execute the mutation
 * - An object with fields that represent the current status of the mutation's execution
 *
 * @param baseOptions options that will be passed into the mutation, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options-2;
 *
 * @example
 * const [cancelReservationMutation, { data, loading, error }] = useCancelReservationMutation({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useCancelReservationMutation(baseOptions?: ApolloReactHooks.MutationHookOptions<CancelReservationMutation, CancelReservationMutationVariables>) {
  return ApolloReactHooks.useMutation<CancelReservationMutation, CancelReservationMutationVariables>(CancelReservationDocument, baseOptions);
}
export type CancelReservationMutationHookResult = ReturnType<typeof useCancelReservationMutation>;
export type CancelReservationMutationResult = Apollo.MutationResult<CancelReservationMutation>;
export type CancelReservationMutationOptions = Apollo.BaseMutationOptions<CancelReservationMutation, CancelReservationMutationVariables>;
export const TravelOfferReservationByIdForCheckoutDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'TravelOfferReservationByIdForCheckout' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'id' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'String' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'travelOfferReservationById' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'id' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'id' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'FragmentSpread', name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' } }] },
          },
        ],
      },
    },
    {
      kind: 'FragmentDefinition',
      name: { kind: 'Name', value: 'TravelOfferReservationForCheckout' },
      typeCondition: { kind: 'NamedType', name: { kind: 'Name', value: 'TravelOfferReservation' } },
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          { kind: 'Field', name: { kind: 'Name', value: 'id' } },
          { kind: 'Field', name: { kind: 'Name', value: 'offerToken' } },
          { kind: 'Field', name: { kind: 'Name', value: 'status' } },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyEventGroupCode' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'code' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'joyDiscountCoupon' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                { kind: 'Field', name: { kind: 'Name', value: 'couponCode' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountAmount' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountCurrency' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedBaseCostMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountedTaxesAndFeesMinorUnits' } },
                { kind: 'Field', name: { kind: 'Name', value: 'isEmailRestricted' } },
                { kind: 'Field', name: { kind: 'Name', value: 'scopeType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'bookerType' } },
                { kind: 'Field', name: { kind: 'Name', value: 'allowedPaidWith' } },
                { kind: 'Field', name: { kind: 'Name', value: 'discountBaseMinorUnits' } },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'confirmation' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'billingContact' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      {
                        kind: 'Field',
                        name: { kind: 'Name', value: 'address' },
                        selectionSet: {
                          kind: 'SelectionSet',
                          selections: [
                            { kind: 'Field', name: { kind: 'Name', value: 'city' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'country' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line1' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'line2' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'postalCode' } },
                            { kind: 'Field', name: { kind: 'Name', value: 'state' } },
                          ],
                        },
                      },
                      { kind: 'Field', name: { kind: 'Name', value: 'email' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'middleName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'phone' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
              ],
            },
          },
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'roomGuests' },
            selectionSet: {
              kind: 'SelectionSet',
              selections: [
                {
                  kind: 'Field',
                  name: { kind: 'Name', value: 'guests' },
                  selectionSet: {
                    kind: 'SelectionSet',
                    selections: [
                      { kind: 'Field', name: { kind: 'Name', value: 'ageClassification' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'firstName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'lastName' } },
                      { kind: 'Field', name: { kind: 'Name', value: 'title' } },
                    ],
                  },
                },
                { kind: 'Field', name: { kind: 'Name', value: 'unitId' } },
              ],
            },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useTravelOfferReservationByIdForCheckoutQuery__
 *
 * To run a query within a React component, call `useTravelOfferReservationByIdForCheckoutQuery` and pass it any options that fit your needs.
 * When your component renders, `useTravelOfferReservationByIdForCheckoutQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTravelOfferReservationByIdForCheckoutQuery({
 *   variables: {
 *      id: // value for 'id'
 *   },
 * });
 */
export function useTravelOfferReservationByIdForCheckoutQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<TravelOfferReservationByIdForCheckoutQuery, TravelOfferReservationByIdForCheckoutQueryVariables>,
) {
  return ApolloReactHooks.useQuery<TravelOfferReservationByIdForCheckoutQuery, TravelOfferReservationByIdForCheckoutQueryVariables>(
    TravelOfferReservationByIdForCheckoutDocument,
    baseOptions,
  );
}
export function useTravelOfferReservationByIdForCheckoutLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<TravelOfferReservationByIdForCheckoutQuery, TravelOfferReservationByIdForCheckoutQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<TravelOfferReservationByIdForCheckoutQuery, TravelOfferReservationByIdForCheckoutQueryVariables>(
    TravelOfferReservationByIdForCheckoutDocument,
    baseOptions,
  );
}
export type TravelOfferReservationByIdForCheckoutQueryHookResult = ReturnType<typeof useTravelOfferReservationByIdForCheckoutQuery>;
export type TravelOfferReservationByIdForCheckoutLazyQueryHookResult = ReturnType<typeof useTravelOfferReservationByIdForCheckoutLazyQuery>;
export type TravelOfferReservationByIdForCheckoutQueryResult = Apollo.QueryResult<TravelOfferReservationByIdForCheckoutQuery, TravelOfferReservationByIdForCheckoutQueryVariables>;
export const SearchTravelOfferReservationsDocument = /*#__PURE__*/ {
  kind: 'Document',
  definitions: [
    {
      kind: 'OperationDefinition',
      operation: 'query',
      name: { kind: 'Name', value: 'SearchTravelOfferReservations' },
      variableDefinitions: [
        {
          kind: 'VariableDefinition',
          variable: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } },
          type: { kind: 'NonNullType', type: { kind: 'NamedType', name: { kind: 'Name', value: 'SearchTravelOfferReservationsPayload' } } },
        },
      ],
      selectionSet: {
        kind: 'SelectionSet',
        selections: [
          {
            kind: 'Field',
            name: { kind: 'Name', value: 'searchTravelOfferReservations' },
            arguments: [{ kind: 'Argument', name: { kind: 'Name', value: 'payload' }, value: { kind: 'Variable', name: { kind: 'Name', value: 'payload' } } }],
            selectionSet: { kind: 'SelectionSet', selections: [{ kind: 'Field', name: { kind: 'Name', value: 'id' } }] },
          },
        ],
      },
    },
  ],
} as unknown as DocumentNode;

/**
 * __useSearchTravelOfferReservationsQuery__
 *
 * To run a query within a React component, call `useSearchTravelOfferReservationsQuery` and pass it any options that fit your needs.
 * When your component renders, `useSearchTravelOfferReservationsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useSearchTravelOfferReservationsQuery({
 *   variables: {
 *      payload: // value for 'payload'
 *   },
 * });
 */
export function useSearchTravelOfferReservationsQuery(
  baseOptions?: ApolloReactHooks.QueryHookOptions<SearchTravelOfferReservationsQuery, SearchTravelOfferReservationsQueryVariables>,
) {
  return ApolloReactHooks.useQuery<SearchTravelOfferReservationsQuery, SearchTravelOfferReservationsQueryVariables>(SearchTravelOfferReservationsDocument, baseOptions);
}
export function useSearchTravelOfferReservationsLazyQuery(
  baseOptions?: ApolloReactHooks.LazyQueryHookOptions<SearchTravelOfferReservationsQuery, SearchTravelOfferReservationsQueryVariables>,
) {
  return ApolloReactHooks.useLazyQuery<SearchTravelOfferReservationsQuery, SearchTravelOfferReservationsQueryVariables>(SearchTravelOfferReservationsDocument, baseOptions);
}
export type SearchTravelOfferReservationsQueryHookResult = ReturnType<typeof useSearchTravelOfferReservationsQuery>;
export type SearchTravelOfferReservationsLazyQueryHookResult = ReturnType<typeof useSearchTravelOfferReservationsLazyQuery>;
export type SearchTravelOfferReservationsQueryResult = Apollo.QueryResult<SearchTravelOfferReservationsQuery, SearchTravelOfferReservationsQueryVariables>;
